import dayjs from "dayjs";
import { sortJobTypes } from "../../helpers/constant";
import {
  generateDeviceType,
  getGaSessionID,
} from "../../helpers/data_management";
import { escapeDoubleQuoteFromPayload } from "../../helpers/escapeDoubleQuoteFromPayload";
import { setCountryApi } from "../../helpers/multicountry_management";
import { sendTrackingEvent } from "../../helpers/tracking_management";
import api, { strapiCall } from "../../utils/api";
import { store } from "../stores/store";
import * as types from "../types/job_type";
import { SET_PDPA_DIALOG_REQUESTED } from "../types/user_type";
import { updateSignInText, updateSignUpText } from "./navbar_action";

const jobListAttributes = `
id
title            
spotlight
keywordHighlight
keywordHighlightText   
imageMobile                 
jobType            
stateRegion
location
bookmark
boosted
haveApplied
salary
expired
slug
active    
externalJobUrl            
activeAt
employerStatus
gptSummary
gptSummaryMs
skills
globalId
globalHireEnabled
globalHirePreferences
tracks {
  id
  title
}
company {
  id
  logo
  name
  registerName  
  verified 
  slug
  hide
  benefits{
    id
    title
  }
}
scraped     
jobSlotType
minYearsExperience
maxYearsExperience
careerLevel
crossBorderData
crossedBorder
category
aggregatedCompanyName
`;

const jobAttributesV1 = `
  id
  title
  image
  spotlight
  keywordHighlight
  keywordHighlightText
  imageThumb
  imageMobile
  imagePosition
  imageSize
  jobType
  globalId
  globalHireEnabled
  globalHirePreferences
  tracks {
    id
    title
  }
  trackId
  stateRegion
  location
  description
  shortDescription
  requirements
  bookmark
  boosted
  haveApplied
  haveRejected
  createdAt
  salary
  expired
  slug
  active    
  externalJobUrl
  secondaryCompanyId
  activeAt
  gptSummary
  gptSummaryMs
  skills
  jobSlotType
  company {
    id
    name
    registerName
    verified
    typeId
    industry
    benefits{
      id
      title
    }
    shortDescription
    coverImage
    coverImageThumb
    jobCount
    bookmark
    hide
    logo
    slug
    lastActiveAt
  }
  scraped
  structuredJobData
  careerLevel
  minYearsExperience
  maxYearsExperience
  crossBorderData
  crossedBorder
  category
  aggregatedCompanyName
`;

const jobAttributesWithCompanyAndProfileV1 = `
  id
  title
  skills
  minYearsExperience
  maxYearsExperience
  image
  imageThumb
  careerLevel
  imageMobile
  imagePosition
  imageSize
  jobType
  tracks {
    id
    title
  }
  trackId
  stateRegion
  location
  description
  shortDescription
  requirements
  bookmark
  haveApplied
  haveRejected
  createdAt
  salary
  expired
  boosted
  slug  
  active
  activeAt
  employerStatus
  externalJobUrl
  secondaryCompanyId
  spotlight
  boosted
  keywordHighlight
  structuredJobData
  gptSummary
  gptSummaryMs
  globalId
  globalHireEnabled
  globalHirePreferences
  company {
    id
    name
    recruitmentFirm
    registerName
    verified
    typeId
    industry
    logo
    shortDescription
    coverImage
    coverImageThumb
    jobCount
    bookmark
    slug
    lastActiveAt
    hide
    benefits {
      title
      description
      iconCode
    }
    profile {
      address
      latitude
      longitude
      descriptions {
        title
        body
      }
      images {
        cover
        image
      }
      videos {
        title
        category
        vimeoId
        quote
        personName
        personPosition
      }
      extras {
        title
        vimeoId
      }
    }
    sizeId
  }
  scraped
  crossBorderData
  crossedBorder
  category
  aggregatedCompanyName
`;

const jobAlertAttributes = `
  id
  title
  frequency
  medium
  owner_id
  created_at
  updated_at
  owner {
    id
    email
  }
`;

const v1JobAlertAttributes = `
  id
  title
  medium
  frequency
  yearOfExperience
  autoCreated
  salary
  activate
  ownerId
  createdAt
  updatedAt
  owner {
    id
    email
  }
`;

const getSpecialisation =
  (params = {}) =>
    async (dispatch) => {
      const payload = {
        query: `{
        specialisations
      }      
      `,
      };

      return api
        .apiCall("/api/job_seeker/v1/graphql", params, payload)
        .then(async (response) => {
          if (response.status === 200) {
            let specialisations = response?.data?.data?.specialisations || [];
            // Rename 'sub_categories' to 'subSpecialisations'
            specialisations = specialisations?.map((specialisation) => {
              const formattedSpecialisation = {
                ...specialisation,
                subSpecialisations: specialisation["sub_categories"] ?? [],
              };
              delete formattedSpecialisation["sub_categories"];
              return formattedSpecialisation;
            });
            return dispatch({
              type: types.GET_SPECIALISATION_SUCCEED,
              payload: specialisations,
            });
          }
        });
    };

const getSpecialisationWithoutRedux = (params = {}) => {
  const payload = {
    query: `{
        specialisations
      }      
      `,
  };

  return api
    .apiCall("/api/job_seeker/v1/graphql", params, payload)
    .then(async (response) => {
      if (response.status === 200) {
        let specialisations = response?.data?.data?.specialisations || [];
        // Rename 'sub_categories' to 'subSpecialisations'
        specialisations = specialisations?.map((specialisation) => {
          const formattedSpecialisation = {
            ...specialisation,
            subSpecialisations: specialisation["sub_categories"] ?? [],
          };
          delete formattedSpecialisation["sub_categories"];
          return formattedSpecialisation;
        });

        if (specialisations != null) {
          return specialisations;
        }
        if (specialisations === null) {
          return { specialisationsNotFound: true };
        }
      }
      return { specialisationsNotFound: true };
    });
};

/*
 * Params:
 * 1) canCancel
 * 2) cancelToken
 */
const getTracks =
  (params = {}) =>
    async (dispatch) => {
      const payload = {
        query: `{
        tracks {
          id
          title
          slug
        }
      }      
    `,
      };

      return api
        .apiCall("/api/job_seeker/v1/graphql", params, payload)
        .then(async (response) => {
          if (response.status === 200) {
            const tracks = response.data.data.tracks;
            return dispatch({
              type: types.GET_TRACK_SUCCEEDED,
              payload: tracks,
            });
          }
        });
    };

const getStateRegions =
  (params = {}) =>
    async (dispatch, getState) => {
      const hasStateRegions = getState()?.jobs?.stateRegions?.length;
      if (hasStateRegions) {
        return;
      }

      // TODO: Fix: Strapi GraphQL suddenly only return 10 records
      // need to explicitly specify the limit
      const payload = {
        query: `
      {
        stateRegions {
          id
          state
          slug
        }
      }      
      `,
      };

      return api
        .apiCall("/api/job_seeker/v1/graphql", params, payload)
        .then(async (response) => {
          if (response.status === 200) {
            let stateRegions = response.data.data?.stateRegions || [];

            const remoteOption = {
              id: "19",
              state: "Remote",
              slug: "remote",
            };

            const exists = stateRegions.some(
              (region) =>
                region.id === remoteOption.id || region.slug === remoteOption.slug
            );

            if (!exists) {
              stateRegions.unshift(remoteOption);
            }

            return dispatch({
              type: types.GET_STATEREGION_SUCCEEDED,
              payload: stateRegions,
            });
          } else {
            return dispatch({
              type: types.GET_STATEREGION_FAILED,
            });
          }
        })
        .catch(() => {
          return dispatch({
            type: types.GET_STATEREGION_FAILED,
          });
        });
    };

const getJobTypes =
  (params = {}) =>
    async (dispatch) => {
      const payload = {
        query: `{
      jobTypes {
          id
          title
        }
      }      
    `,
      };

      return api
        .apiCall("/api/job_seeker/v1/graphql", params, payload)
        .then(async (response) => {
          if (response.status === 200) {
            const jobTypes = sortJobTypes(response.data.data.jobTypes);
            return dispatch({
              type: types.GET_JOB_TYPE_SUCCEEDED,
              payload: jobTypes,
            });
          }
        });
    };

/*
 * Params:
 * 1) canCancel
 * 2) cancelToken
 */
const getExperienceLevels =
  (params = {}) =>
    async (dispatch) => {
      const payload = {
        query: `{
      experiences {
          id
          title
        }
      }      
    `,
      };

      return api
        .apiCall("/api/job_seeker/v1/graphql", params, payload)
        .then(async (response) => {
          if (response.status === 200) {
            const experienceLevels = response.data.data.experiences;
            return dispatch({
              type: types.GET_EXPERIENCE_LEVEL_SUCCEEDED,
              payload: experienceLevels,
            });
          }
        });
    };

const getFeaturedJobs =
  (params = {}) =>
    async (dispatch) => {
      dispatch({ type: types.FETCHING_FEATURED_JOBS });
      const payload = {
        query: `{
        featuredJobs(first: 10) {
          nodes {
            id
            title
          }
        }
      }  
    `,
      };

      return api
        .apiCall("/api/job_seeker/v1/graphql", params, payload)
        .then(async (response) => {
          if (response.status === 200) {
            const getFeaturedJobs = response.data.data.featuredJobs.nodes;

            return dispatch({
              type: types.FETCH_FEATURED_JOBS_SUCCEED,
              payload: getFeaturedJobs ? getFeaturedJobs : [],
            });
          } else {
            return dispatch({
              type: types.FETCH_FEATURED_JOBS_FAILED,
            });
          }
        });
    };

const getCuratedJobs =
  (params = {}) =>
    async (dispatch) => {
      dispatch({ type: types.FETCHING_CURATED_JOBS });

      let payload;

      // Use recommendedJobs query if user is logged in
      if (params.isTitle) {
        payload = {
          query: `{
          recommendedJobs(first:10) {     
            nodes { 
              ${jobListAttributes} 
              image
              tracks {
                id
                title
              }
              careerLevel
              category
            }
          }
        }
        `,
        };
        // Else use curatedJobs query if user is not logged in
      } else {
        let trackInput = params.trackIds
          ? `, trackIds: ${JSON.stringify(params.trackIds)}`
          : "";

        payload = {
          query: `{
      curatedJobs(first:10 ${trackInput}){
        nodes { 
          ${jobListAttributes}   
          image
          tracks {
            id
            title
            }           
          }
          category
        }
      }      
    `,
        };
      }

      return api
        .apiCall("/api/job_seeker/v1/graphql", params, payload)
        .then(async (response) => {
          if (response.status === 200) {
            if (
              response.data?.data?.curatedJobs?.nodes ||
              response.data?.data?.recommendedJobs?.nodes
            ) {
              const getCuratedJobs =
                response.data.data.curatedJobs?.nodes ||
                response.data.data.recommendedJobs?.nodes;

              return dispatch({
                type: types.FETCH_CURATED_JOBS_SUCCEED,
                payload: getCuratedJobs ? getCuratedJobs : [],
              });
            }

            return dispatch({
              type: types.FETCH_CURATED_JOBS_FAILED,
            });
          } else {
            return dispatch({
              type: types.FETCH_CURATED_JOBS_FAILED,
            });
          }
        });
    };

const fetchAshleyBackupJobs =
  (params = {}) =>
    async (dispatch) => {
      let payload;

      payload = {
        query: `{
        recommendedJobs(first:50) {     
          totalCount
          totalPage
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
          nodes {
            ${jobListAttributes}
          }
        }
      }
      `,
      };

      return api
        .apiCall("/api/job_seeker/v1/graphql", params, payload)
        .then(async (response) => {
          if (response.status === 200) {
            const { errors } = response.data;

            const errorObj = Array.isArray(errors) ? errors[0] : errors;

            const { error_code } = errorObj?.extensions || {};

            if (
              (errors !== undefined &&
                errors !== null &&
                Array.isArray(errors) &&
                errors?.length > 0) ||
              (error_code !== undefined && error_code !== null)
            ) {
              return dispatch({
                type: types.FETCH_ASHLEY_RECOMMENDED_JOBS_FAILED,
                errorMessage: Array.isArray(errors)
                  ? errors[0]?.message
                  : "Error",
                currentLoadingJobsKey: params.currentLoadingJobsKey,
                ashleyRecommendsErrorCode: error_code ?? "",
              });
            }

            const pageInfo = response.data.data.recommendedJobs.pageInfo;

            const jobList = response.data.data.recommendedJobs.nodes;
            const filteredJobs = jobList.filter((job) => job !== null);
            const totalPages = response.data.data.recommendedJobs.totalPage;
            const totalJobsCount = response.data.data.recommendedJobs.totalCount;

            return dispatch({
              type: types.FETCH_ASHLEY_RECOMMENDED_JOBS_SUCCEED,
              payload: {
                filteredJobs,
                refresh: params.refresh,
                currentLoadingJobsKey: params.currentLoadingJobsKey,
                totalJobsPages: totalPages,
                totalJobsCount: totalJobsCount,
                ashleyRecommendsError: false,
                pageInfo: pageInfo,
              },
            });
          } else {
            return dispatch({
              type: types.FETCH_ASHLEY_RECOMMENDED_JOBS_FAILED,
              currentLoadingJobsKey: params.currentLoadingJobsKey,
            });
          }
        })
        .catch(() => {
          return dispatch({
            type: types.FETCH_ASHLEY_RECOMMENDED_JOBS_FAILED,
            currentLoadingJobsKey: params.currentLoadingJobsKey,
          });
        });
    };

const clearJobList =
  (params = {}) =>
    async (dispatch) => {
      return new Promise((resolve) => {
        return resolve(
          dispatch({
            type: types.CLEAR_JOB_LIST,
            payload: {
              jobCurrentPage: params?.jobCurrentPage ?? null,
            },
          })
        );
      });
    };

const clearJobSuggestion = () => async (dispatch) => {
  return new Promise((resolve) => {
    return resolve(
      dispatch({
        type: types.CLEAR_JOB_SUGGESTION,
      })
    );
  });
};

const clearBulkJob = () => async (dispatch) => {
  return new Promise((resolve) => {
    return resolve(
      dispatch({
        type: types.CLEAR_BULK_JOB,
      })
    );
  });
};
const clearBulkAppliedJobs = () => async (dispatch) => {
  return new Promise((resolve) => {
    return resolve(
      dispatch({
        type: types.CLEAR_BULK_APPLIED_JOBS,
      })
    );
  });
};

const clearTotalBulkAppliedJobs = () => async (dispatch) => {
  return new Promise((resolve) => {
    return resolve(
      dispatch({
        type: types.CLEAR_TOTAL_BULK_APPLIED_JOBS,
      })
    );
  });
};

const clearAppliedJobs = () => async (dispatch) => {
  return new Promise((resolve) => {
    return resolve(
      dispatch({
        type: types.CLEAR_APPLIED_JOBS,
      })
    );
  });
};

const clearPendingJobs = () => async (dispatch) => {
  return new Promise((resolve) => {
    return resolve(
      dispatch({
        type: types.CLEAR_PENDING_JOBS,
      })
    );
  });
};

const clearArchivedJobs = () => async (dispatch) => {
  return new Promise((resolve) => {
    return resolve(
      dispatch({
        type: types.CLEAR_ARCHIVED_JOBS,
      })
    );
  });
};

const clearBookmarkJobList = () => async (dispatch) => {
  return new Promise((resolve) => {
    return resolve(
      dispatch({
        type: types.CLEAR_JOB_BOOKMARK_LIST,
      })
    );
  });
};

const updateBookmarkedJobInList = (job) => async (dispatch) => {
  return new Promise((resolve) => {
    return resolve(
      dispatch({
        type: types.UPDATE_BOOKMARKED_JOB,
        job: job,
      })
    );
  });
};

const updateInternshipFilterButtonStatus = (params) => async (dispatch) => {
  return new Promise((resolve) => {
    const internshipFilterButtonStatus = params;
    return resolve(
      dispatch({
        type: types.UPDATE_INTERNSHIP_FILTER_BUTTON_STATUS,
        internshipFilterButtonStatus: internshipFilterButtonStatus,
      })
    );
  });
};

/*
 * Params:
 * 1) endCursor
 * 2) trackIds
 * 3) stateRegions
 * 4) jobTypeIds
 * 5) experienceIds
 * 6) expectedSalary
 * 7) canCancel
 * 8) cancelToken
 */

const getJobs =
  (params = {}, pagination = {}, next, prev) =>
    async (dispatch, getState) => {
      dispatch({ type: types.FETCHING_ALL_JOBS, state: true });

      let keywordInput = params.keyword
        ? `keyword: "${escapeDoubleQuoteFromPayload(params.keyword)}",`
        : "";

      let trackInput = params.specialisations
        ? `trackIds: ${JSON.stringify(params.specialisations)},`
        : "";

      let stateRegionInput = params.state
        ? `stateRegions: ${JSON.stringify(params.state)},`
        : "";

      let jobTypeInput = params.jobType
        ? `jobTypeIds: ${JSON.stringify(params.jobType)},`
        : "";

      let experienceInput = params.experience
        ? `experienceIds: ${JSON.stringify(params.experience)},`
        : "";

      let expectedSalaryInput = `expectedSalary: ${params.salary ? params.salary : 0
        },`;

      let companyId = params.companyIds
        ? `companyIds: ${JSON.stringify(params.companyIds)},`
        : "";

      let globalHireEnabled = params.globalHire ? `globalHire: true,` : "";

      let queryInput =
        keywordInput +
        trackInput +
        stateRegionInput +
        jobTypeInput +
        experienceInput +
        companyId +
        expectedSalaryInput +
        globalHireEnabled;

      //Removing trailing ","
      if (queryInput.charAt(queryInput.length - 1) == ",") {
        queryInput = queryInput.substring(0, queryInput.length - 1);
      }

      /**
       * PAGINATION BEHAVIOUR
       *
       * Scenario 1 - going to NEXT page
       * {last: null, first: null, before: "null", after: "endCursor" }
       *
       * Scenario 2 - going to PREVIOUS page
       * {last: 30, first: null, before: "startCursor", after: "" }
       *
       * NOTE: after needs to be an empty string when going to previous page for it to work properly
       */

      const localEndCursor =
        pagination.endCursor === null ? "" : pagination.endCursor;
      const localStartCursor =
        pagination.startCursor === null ? "" : pagination.startCursor;

      const payload = {
        query: `{
        jobListsSearchResults(${queryInput}, last: ${pagination.last}, first: ${pagination.first}, before: "${localStartCursor}", after: "${localEndCursor}", showScraped: true) {
          totalCount
          totalPage
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
          nodes {
            ${jobListAttributes}
            crossBorderData
            category
            aggregatedCompanyName
          }
        }
      }
    `,
      };

      return api
        .apiCall("/api/job_seeker/v1/graphql", params, payload)
        .then(async (response) => {
          if (response.status === 200) {
            if (response.data.data?.jobListsSearchResults) {
              const { jobListsSearchResults } = response.data.data;

              dispatch({
                type: types.FETCH_JOBS_SUCCEED,
                payload: {
                  jobList: jobListsSearchResults.nodes,
                  refresh: params.refresh,
                  currentLoadingJobsKey: params.currentLoadingJobsKey,
                  totalJobsPages: jobListsSearchResults.totalPage,
                  totalJobsCount: jobListsSearchResults.totalCount,
                  pageInfo: jobListsSearchResults.pageInfo,
                },
              });
              return dispatch({ type: types.FETCHING_ALL_JOBS, state: false });
            }
          }

          // prevent the fetch job fail from triggering due to api cancel call
          if (response?.isCancelled !== true) {
            return dispatch({
              type: types.FETCH_JOBS_FAILED,
              currentLoadingJobsKey: params.currentLoadingJobsKey,
            });
          }
        })
        .catch(() => {
          return dispatch({
            type: types.FETCH_JOBS_FAILED,
            currentLoadingJobsKey: params.currentLoadingJobsKey,
          });
        });
    };

const getJobsWithoutRedux = async (params = {}, pagination = {}) => {
  let keywordInput = params.keyword
    ? `keyword: "${escapeDoubleQuoteFromPayload(params.keyword)}",`
    : "";

  let trackInput = params.specialisations
    ? `trackIds: ${JSON.stringify(params.specialisations)},`
    : "";

  let stateRegionInput = params.state
    ? `stateRegions: ${JSON.stringify(params.state)},`
    : "";

  let jobTypeInput = params.jobType
    ? `jobTypeIds: ${JSON.stringify(params.jobType)},`
    : "";

  let experienceInput = params.experience
    ? `experienceIds: ${JSON.stringify(params.experience)},`
    : "";

  let expectedSalaryInput = `expectedSalary: ${params.salary ? params.salary : 0
    },`;

  let companyId = params.companyIds
    ? `companyIds: ${JSON.stringify(params.companyIds)},`
    : "";

  let globalHireEnabled = params.globalHire ? `globalHire: true,` : "";

  let queryInput =
    keywordInput +
    trackInput +
    stateRegionInput +
    jobTypeInput +
    experienceInput +
    companyId +
    expectedSalaryInput +
    globalHireEnabled;

  //Removing trailing ","
  if (queryInput.charAt(queryInput.length - 1) == ",") {
    queryInput = queryInput.substring(0, queryInput.length - 1);
  }

  /**
   * PAGINATION BEHAVIOUR
   *
   * Scenario 1 - going to NEXT page
   * {last: null, first: null, before: "null", after: "endCursor" }
   *
   * Scenario 2 - going to PREVIOUS page
   * {last: 30, first: null, before: "startCursor", after: "" }
   *
   * NOTE: after needs to be an empty string when going to previous page for it to work properly
   */

  const localEndCursor =
    pagination.endCursor === null ? "" : pagination.endCursor;
  const localStartCursor =
    pagination.startCursor === null ? "" : pagination.startCursor;

  const payload = {
    query: `{
      jobListsSearchResults(${queryInput}, last: ${pagination.last}, first: ${pagination.first}, before: "${localStartCursor}", after: "${localEndCursor}", showScraped: true) {
        totalCount
        nodes {
          ${jobListAttributes}
          crossBorderData
          category
          aggregatedCompanyName
        }
      }
    }
  `,
  };
  return api
    .apiCall("/api/job_seeker/v1/graphql", params, payload)
    .then(async (response) => {
      if (response.status === 200 && response.data?.data) {
        const { jobListsSearchResults } = response.data.data;
        if (jobListsSearchResults != null) {
          return {
            jobs: jobListsSearchResults?.nodes,
            pageInfo: jobListsSearchResults?.pageInfo,
            totalJobsCount: jobListsSearchResults?.totalCount,
          };
        }
        if (jobListsSearchResults === null) {
          return { jobNotFound: true };
        }
      }
      return { jobNotFound: true };
    });
};

const getJobsByTracksWithoutRedux = async (trackIds) => {
  let filter = "";

  if (Array.isArray(trackIds) && trackIds.length > 0) {
    filter = `trackIds: ${JSON.stringify(trackIds)},`;
  }

  const payload = {
    query: `{
      jobListsSearchResults(${filter} first: 10000, showScraped: true) {
        totalCount
        edges {
          node {
            slug
            title
            trackId
            globalId
            id
            active
            company {
              name
            }
          }
        }
      }
    }`,
  };

  const response = await api.apiCall("/api/job_seeker/v1/graphql", {}, payload);

  if (response.status === 200) {
    const { jobListsSearchResults } = response.data.data;
    if (jobListsSearchResults) {
      return {
        jobs: jobListsSearchResults.edges
          .map((edge) => edge?.node)
          .filter((node) => node != null),
        totalJobsCount: jobListsSearchResults.totalCount,
      };
    }
  }
  return { jobs: [], totalJobsCount: 0 };
};

const getJobsParams =
  (params = {}) =>
    async (dispatch) => {
      dispatch({ type: types.GET_JOBS_PARAMS, payload: { jobsParams: params } });
    };

/*
 * Params:
 * 1) canCancel
 * 2) cancelToken
 */
const getJobsForSitemap = async (params = {}) => {
  const payload = {
    query: `{
      allJobsNotPaginated(limit:10000){
        slug
        active
        expired
      }
    }`,
  };

  const response = await api.apiCall(
    "/api/job_seeker/v1/graphql",
    params,
    payload,
    true
  );
  if (response.status === 200) {
    const allJobs = response?.data?.data?.allJobsNotPaginated;
    if (allJobs != null) {
      return allJobs;
    }
    if (allJobs === null) {
      return null;
    }
    return null;
  }
};

const getJobsForJobsfeed = async (params = {}) => {
  const payload = {
    query: `{
      allJobs {
        nodes {
          slug
        }
      }
    }`,
  };

  const response = await api.apiCall(
    "/api/job_seeker/v1/graphql",
    params,
    payload,
    true
  );
  if (response.status === 200) {
    const allJobs = response?.data?.data?.allJobs?.nodes;
    if (allJobs != null) {
      return allJobs;
    }
    if (allJobs === null) {
      return null;
    }
    return null;
  }
};

const updateJobListPage =
  (params = {}) =>
    async (dispatch, getState) => {
      dispatch({
        type: types.UPDATE_JOBS_LIST_PAGE,
        payload: params,
      });
    };

const updateFetchingJobListStatus =
  (params = {}) =>
    async (dispatch, getState) => {
      dispatch({
        type: types.UPDATE_FETCHING_ALL_JOBS,
        payload: params,
      });
    };

/*
 * Params:
 * 1) canCancel
 * 2) cancelToken
 */
const getJobsForCima = async (params = {}) => {
  const payload = {
    query: `{
      cimaJobs {      
        nodes {
          id
          shortDescription
          description
          requirements
          title
          location
          tracks {
            title
          }
          image
          salary
          jobType
          updatedAt
          slug
          shortDescription
          requirements
          structuredJobData
          company {
            id
            name
            jobCount
            slug
            firstBillingAddress {
              country
              streetAddress
              town
              postcode
              state
            }
          }
        }
      }
    }`,
  };

  return api
    .apiCall("/api/job_seeker/v1/graphql", params, payload, true)
    .then(async (response) => {
      if (response.status === 200) {
        const jobs = response.data.data.nodes;
        if (jobs != null) {
          return jobs;
        }
        if (jobs === null) {
          return null;
        }
        return null;
      }
    });
};

const getJobV1 =
  (params = {}) =>
    async (dispatch) => {
      dispatch({ type: types.GET_JOB });

      const payload = {
        query: `{
    job(id: "${params.jobId}"){
      ${jobAttributesWithCompanyAndProfileV1}
      crossBorderData
      category
      aggregatedCompanyName
    }
  }
`,
      };

      const response = await api.apiCall(
        "/api/job_seeker/v1/graphql",
        params,
        payload,
        true
      );
      if (response.status === 200) {
        const { job } = response.data.data;

        if (job != null) {
          return dispatch({
            type: types.GET_JOB_SUCCEED,
            job: job,
          });
        }
        if (job === null) {
          return dispatch({
            type: types.GET_JOB_FAILED,
            job: job,
            jobNotFound: true,
          });
        }
      }
      return await dispatch({ type: types.GET_JOB_FAILED, jobId: params.jobId });
    };

const getBulkJob =
  (params = {}) =>
    async (dispatch) => {
      dispatch({ type: types.GET_BULK_JOB });

      const payload = {
        query: `{
        jobs(ids: ${JSON.stringify(params.jobIds)}) {
          ${jobListAttributes}
        }
      }`,
      };

      const response = await api.apiCall(
        "/api/job_seeker/v1/graphql",
        params,
        payload,
        true
      );

      if (response.status === 200) {
        const { jobs } = response.data.data;

        if (jobs != null) {
          return dispatch({
            type: types.GET_BULK_JOB_SUCCEED,
            jobs: jobs,
          });
        }
        if (jobs === null) {
          return dispatch({
            type: types.GET_BULK_JOB_FAILED,
            jobs: jobs,
            jobNotFound: true,
          });
        }
      }
    };
/*
 * Params:
 * 1) endCursor
 * 2) reload
 * 3) canCancel
 * 4) cancelToken
 */
const fetchJobBookmarkList =
  (params = {}) =>
    (dispatch) => {
      dispatch({ type: types.FETCH_JOB_BOOKMARK_LIST });

      const apiUrl = setCountryApi(params);

      const payload = {
        query: `{
        bookmarkJobs(after: "${params.endCursor}", first: ${params.first}) {
          pageInfo {
            startCursor
            endCursor
            hasPreviousPage
            hasNextPage
          }
          nodes {
            id
            createdAt
            job {
              ${jobListAttributes}
              image
              tracks {
                id
                title
              }
              category
            }
          }
        }
      }
      
    `,
      };

      return api.apiCall(apiUrl, params, payload).then(async (response) => {
        if (response.status === 200) {
          if (
            response.data &&
            response.data.data &&
            response.data.data.bookmarkJobs
          ) {
            const { nodes, pageInfo } = response.data.data.bookmarkJobs;
            if (nodes != null) {
              return dispatch({
                type: types.FETCH_JOB_BOOKMARK_LIST_SUCCEED,
                payload: { nodes, pageInfo },
              });
            }
          }
        }
        return dispatch({ type: types.FETCH_JOB_BOOKMARK_LIST_FAILED });
      });
    };

/*
 * Params:
 * 1) endCursor
 * 2) reload
 * 3) canCancel
 * 4) cancelToken
 */
const fetchAppliedJobs =
  (params = {}) =>
    (dispatch) => {
      dispatch({ type: types.FETCHING_APPLIED_JOBS });

      const apiUrl = setCountryApi(params);

      const payload = {
        query: `{
      appliedJobs(after: "${params.endCursor}", first: ${params.first}) {
        pageInfo {
          startCursor
          endCursor
          hasPreviousPage
          hasNextPage
        }
        nodes {
          id
          applicationStatus
          status
          createdAt
          job {
            ${jobListAttributes}
            image
            tracks {
              id
              title
            }           
          }
        }
        totalCount
      }
    }
  `,
      };
      return api.apiCall(apiUrl, params, payload).then(async (response) => {
        if (response.status === 200) {
          if (
            response.data &&
            response.data.data &&
            response.data.data.appliedJobs
          ) {
            const { nodes, pageInfo } = response.data.data.appliedJobs;
            if (nodes != null) {
              return dispatch({
                type: types.FETCH_APPLIED_JOBS_SUCCEED,
                payload: { nodes, pageInfo },
              });
            }
          }
        }
        return dispatch({ type: types.FETCH_APPLIED_JOBS_FAILED });
      });
    };

/*
 * Params:
 * 1) endCursor
 * 2) reload
 * 3) canCancel
 * 4) cancelToken
 */
const fetchPendingJobs =
  (params = {}) =>
    (dispatch) => {
      dispatch({ type: types.FETCHING_PENDING_JOBS });
      const apiUrl = setCountryApi(params);

      const payload = {
        query: `{
      pendingJobs(after: "${params.endCursor}", first: ${params.first}) {
        pageInfo {
          startCursor
          endCursor
          hasPreviousPage
          hasNextPage
        }
        nodes {
          id    
          createdAt
          job {
            ${jobListAttributes}
            image
            tracks {
              id
              title
            }           
          }
        }
      }
    }
  `,
      };

      return api.apiCall(apiUrl, params, payload).then(async (response) => {
        if (response.status === 200) {
          if (
            response.data &&
            response.data.data &&
            response.data.data.pendingJobs
          ) {
            const { nodes, pageInfo } = response.data.data.pendingJobs;
            if (nodes != null) {
              return dispatch({
                type: types.FETCH_PENDING_JOBS_SUCCEED,
                payload: { nodes, pageInfo },
              });
            }
          }
        }
        return dispatch({ type: types.FETCH_PENDING_JOBS_FAILED });
      });
    };

/*
 * Params:
 * 1) endCursor
 * 2) reload
 * 3) canCancel
 * 4) cancelToken
 */
const fetchArchivedJobs =
  (params = {}) =>
    (dispatch) => {
      dispatch({ type: types.FETCHING_ARCHIVED_JOBS });
      const apiUrl = setCountryApi(params);

      const payload = {
        query: `{
      archiveJobs(after: "${params.endCursor}", first: ${params.first}) {
        pageInfo {
          startCursor
          endCursor
          hasPreviousPage
          hasNextPage
        }
        nodes {
          id    
          createdAt
          job {
            ${jobListAttributes}
            image
            tracks {
              id
              title
            }           
          }
        }
      }
    }
  `,
      };

      return api.apiCall(apiUrl, params, payload).then(async (response) => {
        if (response.status === 200) {
          if (
            response.data &&
            response.data.data &&
            response.data.data.archiveJobs
          ) {
            const { nodes, pageInfo } = response.data.data.archiveJobs;
            if (nodes != null) {
              return dispatch({
                type: types.FETCH_ARCHIVED_JOBS_SUCCEED,
                payload: { nodes, pageInfo },
              });
            }
          }
        }
        return dispatch({ type: types.FETCH_ARCHIVED_JOBS_FAILED });
      });
    };

/*
 * Params:
 * 1) jobId
 * 2) canCancel
 * 3) cancelToken
 */

const bookmarkJob =
  (params = {}) =>
    async (dispatch) => {
      dispatch({ type: types.BOOKMARK_JOB });

      const gaSessionId = getGaSessionID();

      const payload = {
        query: `
        mutation {
          bookmarkJob(input: {jobId: "${params.jobId}", sessionId: "${gaSessionId}"}) {
            bookmarkJob {
              id
              createdAt
              job {
                ${jobAttributesV1}
              }
            }
          }
        }      
      `,
      };

      const response = await api.apiCall(
        "/api/job_seeker/v1/graphql",
        params,
        payload
      );

      if (response.status === 200) {
        const { bookmarkJob } = response?.data?.data;
        if (bookmarkJob != null && bookmarkJob != undefined) {
          let cJob;
          let cBookmarkJob = bookmarkJob.bookmarkJob;
          if (cBookmarkJob && cBookmarkJob.job) {
            cJob = cBookmarkJob.job;
          }
          // Custom event for saving/bookmarking jobs

          let categoryTag;

          if (
            cJob?.boosted === true &&
            cJob?.spotlight === true &&
            cJob?.keywordHighlight === true
          ) {
            categoryTag = "featured_spotlight_keyword";
          } else if (
            cJob?.boosted === true &&
            cJob?.spotlight === true &&
            !cJob?.keywordHighlight
          ) {
            categoryTag = "featured_spotlight";
          } else if (
            cJob?.boosted === true &&
            !cJob?.spotlight &&
            cJob?.keywordHighlight === true
          ) {
            categoryTag = "featured_keyword";
          } else if (
            !cJob?.boosted &&
            cJob?.spotlight === true &&
            cJob?.keywordHighlight === true
          ) {
            categoryTag = "spotlight_keyword";
          } else if (
            !cJob?.boosted &&
            !cJob?.spotlight &&
            cJob?.keywordHighlight === true
          ) {
            categoryTag = "keyword";
          } else if (
            cJob?.boosted === true &&
            !cJob?.spotlight &&
            !cJob?.keywordHighlight
          ) {
            categoryTag = "featured";
          } else if (
            !cJob?.boosted &&
            cJob?.spotlight === true &&
            !cJob?.keywordHighlight
          ) {
            categoryTag = "spotlight";
          }

          !params.appliedDialog &&
            params.origin &&
            params?.origin !== "for-you" &&
            sendTrackingEvent({
              event: `${cJob?.boosted
                ? `CE_saved-job-${params.origin}`
                : cJob?.secondaryCompanyId
                  ? `${params.origin}-urgent-hiring-job-saved`
                  : `CE_saved-job-${params.origin}`
                }`,
              "company-id": `${cJob?.company?.id}`,
              "job-id": `${cJob?.id}`,
              "job-title": cJob.title,
              spotlight: cJob?.spotlight ? true : false,
              featured: cJob?.boosted ? true : false,
              keyword: cJob?.keywordHighlight ? true : false,
            });

          params.appliedDialog === true &&
            sendTrackingEvent({
              event: "CE_click-save-job-job-application-popup",
              "job-id": cJob.id,
              "job-title": cJob.title,
              save: true,
            });

          return dispatch({
            type: types.BOOKMARK_JOB_SUCCEED,
            payload: { bookmarkJob: cJob },
          });
        }
        if (bookmarkJob === null) {
          return dispatch({
            type: types.BOOKMARK_JOB_FAILED,
            payload: { message: "ERROR" },
          });
        }
      }
      return await dispatch({
        type: types.BOOKMARK_JOB_FAILED,
        payload: { message: "ERROR" },
      });
    };

/*
 * Params:
 * 1) jobId
 * 2) canCancel
 * 3) cancelToken
 */
const unBookmarkJob =
  (params = {}) =>
    async (dispatch) => {
      dispatch({ type: types.UNBOOKMARK_JOB });
      const payload = {
        query: `
      mutation {
        unBookmarkJob(input: {jobId: "${params.jobId}"}) {
          success
          job {
            ${jobAttributesV1}
          }
        }
      }      
      `,
      };

      let response;

      if (params.useFullUrl) {
        const apiUrl = setCountryApi(params);
        response = await api.apiCall(apiUrl, params, payload);
      } else {
        response = await api.apiCall(
          "/api/job_seeker/v1/graphql",
          params,
          payload
        );
      }

      if (response.status === 200) {
        const { unBookmarkJob } = response.data.data;
        if (unBookmarkJob != null) {
          // Custom event for unsaving/unbookmarking jobs
          const cJob = unBookmarkJob.job;

          let categoryTag;

          if (
            cJob?.boosted === true &&
            cJob?.spotlight === true &&
            cJob?.keywordHighlight === true
          ) {
            categoryTag = "featured_spotlight_keyword";
          } else if (
            cJob?.boosted === true &&
            cJob?.spotlight === true &&
            !cJob?.keywordHighlight
          ) {
            categoryTag = "featured_spotlight";
          } else if (
            cJob?.boosted === true &&
            !cJob?.spotlight &&
            cJob?.keywordHighlight === true
          ) {
            categoryTag = "featured_keyword";
          } else if (
            !cJob?.boosted &&
            cJob?.spotlight === true &&
            cJob?.keywordHighlight === true
          ) {
            categoryTag = "spotlight_keyword";
          } else if (
            !cJob?.boosted &&
            !cJob?.spotlight &&
            cJob?.keywordHighlight === true
          ) {
            categoryTag = "keyword";
          } else if (
            cJob?.boosted === true &&
            !cJob?.spotlight &&
            !cJob?.keywordHighlight
          ) {
            categoryTag = "featured";
          } else if (
            !cJob?.boosted &&
            cJob?.spotlight === true &&
            !cJob?.keywordHighlight
          ) {
            categoryTag = "spotlight";
          }

          !params.appliedDialog &&
            params.origin &&
            sendTrackingEvent({
              event: `${cJob.boosted
                ? `CE_unsaved-job-${params.origin}`
                : cJob.secondary_company_id
                  ? `${params.origin}-urgent-hiring-job-unsaved`
                  : `CE_unsaved-job-${params.origin}`
                }`,
              "company-id": `${cJob.company.id}`,
              "job-id": `${cJob.id}`,
              spotlight: cJob?.spotlight ? true : false,
              featured: cJob?.boosted ? true : false,
              keyword: cJob?.keywordHighlight ? true : false,
            });

          params.appliedDialog === true &&
            sendTrackingEvent({
              event: "CE_click-save-job-job-application-popup",
              "job-id": cJob.id,
              "job-title": cJob.title,
              save: false,
            });

          return dispatch({
            type: types.UNBOOKMARK_JOB_SUCCEED,
            job: unBookmarkJob ? unBookmarkJob.job : null,
            jobId: params.jobId,
          });
        } else {
          return dispatch({
            type: types.UNBOOKMARK_JOB_FAILED,
            payload: { message: "ERROR" },
          });
        }
      }
      return await dispatch({
        type: types.UNBOOKMARK_JOB_FAILED,
        payload: { message: "ERROR" },
      });
    };

/*
 * Params:
 * 1) jobId
 * 2) updateList
 * 3) updateRejectedList
 * 3) canCancel
 * 4) cancelToken
 */
const applyJob =
  (params = {}) =>
    async (dispatch) => {
      dispatch({ type: SET_PDPA_DIALOG_REQUESTED, payload: true });
      dispatch({ type: types.APPLY_JOB });

      const deviceType = generateDeviceType();

      const gaSessionId = getGaSessionID();
      const trackingParameters = params.trackingParameters || null;
      const trackingParameterFields = [];
      if (trackingParameters?.source) {
        trackingParameterFields.push(
          `source: "${escapeDoubleQuoteFromPayload(trackingParameters.source)}"`
        );
      }
      if (trackingParameters?.medium) {
        trackingParameterFields.push(
          `medium: "${escapeDoubleQuoteFromPayload(trackingParameters.medium)}"`
        );
      }
      if (trackingParameters?.campaign) {
        trackingParameterFields.push(
          `campaign: "${escapeDoubleQuoteFromPayload(
            trackingParameters.campaign
          )}"`
        );
      }
      if (trackingParameters?.term) {
        trackingParameterFields.push(
          `term: "${escapeDoubleQuoteFromPayload(trackingParameters.term)}"`
        );
      }
      const jobApplicationMetadata =
        trackingParameterFields.length > 0
          ? `jobApplicationMetadata: { tracking_parameters: { ${trackingParameterFields.join(
            ", "
          )} } },`
          : "";

      const payload = {
        query: `mutation {
      applyJob(input: {
        jobId: "${params.jobId}",
        deviceType: "${deviceType}",
        sessionId: "${gaSessionId}",
        applySource: "${params.applySource}",
        ${jobApplicationMetadata}
        }) {
        applyJob {
          id          
          job {
            ${jobAttributesV1}
          }
        }      
      }
    }
  `,
      };

      let response;

      if (params.useFullUrl) {
        const apiUrl = setCountryApi(params);
        response = await api.apiCall(apiUrl, params, payload);
      } else {
        response = await api.apiCall(
          "/api/job_seeker/v1/graphql",
          params,
          payload
        );
      }

      if (response.status === 200) {
        const { errors } = response.data;
        if (errors) {
          return dispatch({
            type: types.APPLY_JOB_FAILED,
            payload: { message: errors[0].message },
          });
        }

        if (response.data.data.applyJob != null) {
          let cJob;
          let appliedJob = response.data.data.applyJob.applyJob;
          let jobBucketId = appliedJob.id ?? "";
          if (appliedJob && appliedJob.job) {
            cJob = appliedJob.job;
          }

          return dispatch({
            type: types.APPLY_JOB_SUCCEED,
            payload: {
              jobBucketId: jobBucketId,
              appliedJob: cJob,
              updateList: params.updateList,
              updateRejectedList: params.updateRejectedList,
              updateRecommendedList: params.updateRecommendedList,
              jobApplication: appliedJob,
              triggerNPS: response.data.data.applyJob.triggerNPS, //Not sure whether we need for Web at the moment
            },
          });
        }
      }
      return dispatch({
        type: types.APPLY_JOB_FAILED,
        payload: { message: "Sorry there was a problem with your request" },
      });
    };

const applyJobs =
  (params = {}) =>
    (dispatch) => {
      dispatch({ type: SET_PDPA_DIALOG_REQUESTED, payload: true });
      dispatch({ type: types.APPLY_JOBS });

      const deviceType = generateDeviceType();

      const payload = {
        query: `mutation {
        bulkApplyJob(input: {
          jobIds: ${JSON.stringify(params.jobIds)},
          deviceType: "${deviceType}"
        }) {
        applyJobs {                 
          id
          job {
            ${jobAttributesV1}
          }
        }
        triggerNps        
        success
      }
    }
  `,
      };

      return api
        .apiCall("/api/job_seeker/v1/graphql", params, payload)
        .then(async (response) => {
          if (response.status === 200) {
            if (response.data.data.bulkApplyJob != null) {
              return dispatch({
                type: types.APPLY_JOBS_SUCCEED,
                success: response.data.data.bulkApplyJob.success,
                appliedJobs: response.data.data.bulkApplyJob.applyJobs,
              });
            }
          }
          return dispatch({
            type: types.APPLY_JOBS_FAILED,
            payload: { message: "Sorry there was a problem with your request" },
          });
        })
        .catch(() => {
          return dispatch({
            type: types.APPLY_JOBS_FAILED,
            payload: { message: "Sorry there was a problem with your request" },
          });
        });
    };

const bulkApplyJobs =
  (params = {}) =>
    (dispatch, getState) => {
      dispatch({ type: SET_PDPA_DIALOG_REQUESTED, payload: true });
      dispatch({ type: types.BULK_APPLY_JOBS });

      const deviceType = generateDeviceType();

      const payload = {
        query: `mutation {
        bulkApplyJob(input: {
          jobIds: ${JSON.stringify(params.jobIds)},
          deviceType: "${deviceType}",
          applySource: "${params.applySource}",
        }) {
        applyJobs {                 
          id
          job {
            ${jobAttributesV1}
          }
        }
        triggerNps        
        success
      }
    }
  `,
      };

      return api
        .apiCall("/api/job_seeker/v1/graphql", params, payload)
        .then(async (response) => {
          if (response.status === 200) {
            if (response.data.data.bulkApplyJob != null) {
              const appliedJobs = response.data.data.bulkApplyJob.applyJobs;
              const currentTotalAppliedJobs =
                getState().jobs.bulkAppliedJobsTotal || [];
              return dispatch({
                type: types.BULK_APPLY_JOBS_SUCCEED,
                success: response.data.data.bulkApplyJob.success,
                bulkAppliedJobs: appliedJobs,
                bulkAppliedJobsTotal: [
                  ...currentTotalAppliedJobs,
                  ...appliedJobs,
                ],
              });
            }
          }
          return dispatch({
            type: types.BULK_APPLY_JOBS_FAILED,
            payload: { message: "Sorry there was a problem with your request" },
          });
        })
        .catch(() => {
          return dispatch({
            type: types.BULK_APPLY_JOBS_FAILED,
            payload: { message: "Sorry there was a problem with your request" },
          });
        });
    };

/*
 * Fetch all internship jobs grouped by specialisation
 * Returns: Array of jobs with specialisation information
 */
const getInternshipJobs =
  (params = {}) =>
    async (dispatch) => {
      dispatch({ type: types.FETCHING_INTERNSHIP_JOBS });

      const payload = {
        query: `{
        internshipJobs
      }`,
      };

      return api
        .apiCall("/api/job_seeker/v1/graphql", params, payload)
        .then(async (response) => {
          if (response.status === 200) {
            if (
              response.data &&
              response.data.data &&
              response.data.data.internshipJobs
            ) {
              const internshipJobs = response.data.data.internshipJobs;
              return dispatch({
                type: types.FETCH_INTERNSHIP_JOBS_SUCCEED,
                payload: { internshipJobs },
              });
            }
          }
          return dispatch({
            type: types.FETCH_INTERNSHIP_JOBS_FAILED,
            payload: { message: "Failed to fetch internship jobs" },
          });
        })
        .catch((error) => {
          return dispatch({
            type: types.FETCH_INTERNSHIP_JOBS_FAILED,
            payload: {
              message: error?.message || "Failed to fetch internship jobs",
            },
          });
        });
    };

/*
 * Apply to all jobs within selected specialisation(s)
 * Params:
 * 1) specialisationIds - Array of specialisation IDs to apply
 * 2) applyFromInternshipPage - Boolean to track if applying from internship page
 * 3) canCancel
 * 4) cancelToken
 */
const applyJobsBySpecialisation =
  (params = {}) =>
    async (dispatch) => {
      dispatch({ type: SET_PDPA_DIALOG_REQUESTED, payload: true });
      dispatch({ type: types.APPLY_JOB_SPECIALISATION });

      const payload = {
        query: `mutation ApplyJobSpecialisation {
        applyJobSpecialisation(input: {
          specialisationIds: ${JSON.stringify(params.specialisationIds)},
          applyFromInternshipPage: ${params.applyFromInternshipPage !== false},
          applySource: ${JSON.stringify(params.applySource)}
        }) {
          overuser
          success
          applyJobs {
            createdAt
            deviceType
            id
            jobId
            updatedAt
            userId
          }
        }
      }`,
      };

      return api
        .apiCall("/api/job_seeker/v1/graphql", params, payload)
        .then(async (response) => {
          if (response.status === 200) {
            if (
              response.data &&
              response.data.data &&
              response.data.data.applyJobSpecialisation
            ) {
              const result = response.data.data.applyJobSpecialisation;

              // // Track the application
              // if (result.success && result.applyJobs?.length > 0) {
              //   sendTrackingEvent({
              //     event: "bulk_apply_specialisation",
              //     properties: {
              //       specialisation_ids: params.specialisationIds,
              //       jobs_applied: result.applyJobs.length,
              //       apply_from_internship_page:
              //         params.applyFromInternshipPage !== false,
              //     },
              //   });
              // }

              return dispatch({
                type: types.APPLY_JOB_SPECIALISATION_SUCCEED,
                payload: {
                  success: result.success,
                  overuser: result.overuser,
                  appliedJobs: result.applyJobs,
                  specialisationIds: params.specialisationIds,
                },
              });
            }
          }
          return dispatch({
            type: types.APPLY_JOB_SPECIALISATION_FAILED,
            payload: { message: "Failed to apply to jobs by specialisation" },
          });
        })
        .catch((error) => {
          return dispatch({
            type: types.APPLY_JOB_SPECIALISATION_FAILED,
            payload: {
              message:
                error?.message || "Failed to apply to jobs by specialisation",
            },
          });
        });
    };

/*
 * Params:
 * 1) jobApplicationId
 * 2) canCancel
 * 3) cancelToken
 */
const cancelJobApplication =
  (params = {}) =>
    (dispatch) => {
      dispatch({ type: types.CANCELLING_JOB_APPLICATION });

      const apiUrl = setCountryApi(params);

      const payload = {
        query: `mutation {
        unApplyJob(input: {id: "${params.jobApplicationId}"}) {
          unApplyJob {
            id
            job {
              ${jobListAttributes}
              image
              tracks {
                id
                title
              } 
            }
          }
        }
      }
    `,
      };

      return api
        .apiCall(apiUrl, params, payload)
        .then(async (response) => {
          let jobApplicationId = params.jobApplicationId;
          if (response.status === 200 && response.data.data.unApplyJob) {
            const { unApplyJob } = response.data.data.unApplyJob;
            if (unApplyJob != null) {
              return dispatch({
                type: types.CANCEL_JOB_APPLICATION_SUCCEED,
                jobApplication: unApplyJob,
                jobApplicationId: params.jobApplicationId,
              });
            }
            if (unApplyJob === null) {
              return dispatch({
                type: types.CANCEL_JOB_APPLICATION_FAILED,
                payload: {
                  message: "ERROR",
                  jobApplicationId: jobApplicationId,
                },
              });
            }
          }

          if (response.data.errors && response.data.errors.length > 0) {
            if (
              response.data.errors[0].message &&
              !response.data.errors[0].message.includes(
                "Couldn't find JobBucket with"
              )
            ) {
              jobApplicationId = "-1";
            }
          }

          return dispatch({
            type: types.CANCEL_JOB_APPLICATION_FAILED,
            payload: {
              message: "ERROR",
              jobApplicationId: jobApplicationId,
            },
          });
        })
        .catch(() => {
          return dispatch({
            type: types.CANCEL_JOB_APPLICATION_FAILED,
            payload: {
              message: "ERROR",
              jobApplicationId: params.jobApplicationId,
            },
          });
        });
    };

const updateHoverJob = (hoverJob) => async (dispatch) => {
  return new Promise((resolve) => {
    return resolve(
      dispatch({
        type: types.UPDATE_HOVER_JOB,
        payload: hoverJob,
      })
    );
  });
};

const updateSelectedJob = (selectedJob) => async (dispatch) => {
  return new Promise((resolve) => {
    return resolve(
      dispatch({
        type: types.UPDATE_SELECTED_JOB,
        selectedJob: selectedJob,
      })
    );
  });
};

const updateSelectedBulkJobs = (selectedJobs) => async (dispatch) => {
  return dispatch({
    type: types.UPDATE_SELECTED_BULK_JOBS,
    selectedJobs: selectedJobs,
  });
};

const updateMissingCountryPreference =
  (missingCountryPreference) => async (dispatch) => {
    return dispatch({
      type: types.UPDATE_MISSING_COUNTRY_PREFERENCE,
      missingCountryPreference: missingCountryPreference,
    });
  };

const updateJobId = (jobId) => async (dispatch) => {
  return new Promise((resolve) => {
    return resolve(
      dispatch({
        type: types.UPDATE_JOB_ID,
        jobId: jobId,
      })
    );
  });
};

const updateJobList = (jobs) => async (dispatch) => {
  return new Promise((resolve) => {
    return resolve(
      dispatch({
        type: types.UPDATE_JOBS_LIST,
        payload: jobs,
      })
    );
  });
};

const updateJobListFilter = (jobListFilter) => async (dispatch) => {
  return new Promise((resolve) => {
    return resolve(
      dispatch({
        type: types.UPDATE_JOB_LIST_FILTER,
        jobListFilter: jobListFilter,
      })
    );
  });
};

const updateJobListFilterLanding =
  (jobListFilterLanding) => async (dispatch) => {
    return new Promise((resolve) => {
      return resolve(
        dispatch({
          type: types.UPDATE_JOB_LIST_FILTER_LANDING,
          payload: jobListFilterLanding,
        })
      );
    });
  };

const updateSelectAllJobFilter = (filters) => async (dispatch) => {
  return new Promise((resolve) => {
    return resolve(
      dispatch({
        type: types.UPDATE_SELECT_ALL_JOB_FILTER,
        filters: filters,
      })
    );
  });
};

// For SEO
/*
 * Params:
 * 1) jobId
 * 2) canCancel
 * 3) cancelToken
 */
const getJobWithoutRedux = (params = {}) => {
  const payload = {
    query: `{
      job(id: "${params.jobId}"){
        ${jobAttributesWithCompanyAndProfileV1}
        crossBorderData
        category
        aggregatedCompanyName
      }
    }
  `,
  };

  return api
    .apiCall("/api/job_seeker/v1/graphql", params, payload, true)
    .then(async (response) => {
      if (response.status !== 200) {
        throw new Error("Failed to fetch job");
      }

      if (response.data?.errors?.length) {
        throw new Error("Job query failed");
      }

      const job = response.data?.data?.job;

      if (job === null) {
        return { jobNotFound: true };
      }

      if (!job) {
        throw new Error("Unexpected job response");
      }

      return job;
    });
};

const getTracksWithoutRedux = (params = {}) => {
  const payload = {
    query: `{
        tracks {
          id
          title
          slug
        }
      }      
    `,
  };

  return api
    .apiCall("/api/job_seeker/v1/graphql", params, payload)
    .then(async (response) => {
      if (response.status === 200) {
        const tracks = response.data?.data?.tracks;
        return tracks;
      } else {
        return [];
      }
    })
    .catch((error) => []);
};

const getStateRegionWithoutRedux = async (params = {}) => {
  const payload = {
    query: `
      {
        stateRegions {
          id
          state
          slug
        }
      }      
      `,
  };

  return api
    .apiCall("/api/job_seeker/v1/graphql", params, payload)
    .then(async (response) => {
      if (response.status === 200) {
        const stateRegions = response.data.data.stateRegions;
        return stateRegions;
      }
    });
};

const getJobTypesWithoutRedux = (params = {}) => {
  const payload = {
    query: `{
      jobTypes {
          id
          title
        }
      }      
    `,
  };

  return api
    .apiCall("/api/job_seeker/v1/graphql", params, payload)
    .then(async (response) => {
      if (response.status === 200) {
        return sortJobTypes(response.data.data.jobTypes);
      }
    });
};

const getExperienceLevelsWithoutRedux = (params = {}) => {
  const payload = {
    query: `{
      experiences {
          id
          title
        }
      }      
    `,
  };

  return api
    .apiCall("/api/job_seeker/v1/graphql", params, payload)
    .then(async (response) => {
      if (response.status === 200) {
        const experienceLevels = response.data.data.experiences;
        return experienceLevels;
      }
    });
};

const getJobTrackSlugWithoutRedux = (params = {}) => {
  const payload = {
    query: `{
      tracks {
        id
        slug
      }
    }      
  `,
  };

  return api
    .apiCall("/api/job_seeker/v1/graphql", params, payload, true)
    .then(async (response) => {
      if (response.status === 200) {
        const job_track_slug = response.data.data.tracks;
        if (job_track_slug != null) {
          return job_track_slug;
        }
        if (job_track_slug === null) {
          return null;
        }
      }
      return null;
    });
};

const getJobAppliedAndBookmarkStatus =
  (params = {}) =>
    async (dispatch) => {
      dispatch({ type: types.GET_JOB_BOOKMARK_AND_APPLY_STATUS });

      const payload = {
        query: `{
      job(id: "${params.jobId}"){
        bookmark
        haveApplied
      }
    }
  `,
      };

      return api
        .apiCall("/api/job_seeker/v1/graphql", params, payload)
        .then(async (response) => {
          if (response.status === 200) {
            if (response.data?.data?.job) {
              const { bookmark, haveApplied } = response.data.data.job;

              dispatch({ type: types.GET_JOB_BOOKMARK_AND_APPLY_STATUS_SUCCEED });
              return { bookmark, haveApplied };
            }
          }

          dispatch({ type: types.GET_JOB_BOOKMARK_AND_APPLY_STATUS_FAILED });
          return null;
        });
    };

const updateBookmarkedRecommendedJobs =
  (recommendedJobs) => async (dispatch) => {
    return dispatch({
      type: types.UPDATE_RECOMMENDED_JOBS,
      bookmarkedRecommendedJobs: recommendedJobs,
    });
  };

const getRecommendedJobs =
  (params = {}) =>
    async (dispatch) => {
      dispatch({ type: types.FETCHING_ONBOARDING_RECOMMENDED_JOBS });

      const payload = {
        query: `{
        recommendedJobs {
          totalCount
          pageInfo {
            startCursor
            hasNextPage
            hasPreviousPage
            endCursor
          }
          edges {
            node {
              ${jobAttributesV1}
            }
          }
        }
      }`,
      };

      return api
        .apiCall("/api/job_seeker/v1/graphql", params, payload)
        .then(async (response) => {
          if (response.status === 200) {
            if (response.data?.data?.recommendedJobs) {
              const jobs = response.data.data.recommendedJobs;
              return dispatch({
                type: types.FETCH_ONBOARDING_RECOMMENDED_JOBS_SUCCEED,
                jobs: jobs?.edges ? jobs.edges : [],
              });
            }
            return dispatch({
              type: types.FETCH_ONBOARDING_RECOMMENDED_JOBS_FAILED,
            });
          } else {
            return dispatch({
              type: types.FETCH_ONBOARDING_RECOMMENDED_JOBS_FAILED,
            });
          }
        });
    };

const getInternshipBankJobs =
  (params = {}) =>
    async (dispatch) => {
      dispatch({ type: types.FETCHING_RECOMMENDED_JOBS });

      const payload = {
        query: `{
        internshipJobLists(
          trackIds: ${JSON.stringify(params.specialisations)}
          ){
            maxPageSize
            totalCount
            totalPage
            nodes {
              ${jobListAttributes}
            }
          }
      }`,
      };

      // 🕒 Set timeout to abort after 5 seconds
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // abort after 5s

      try {
        const response = await api.apiCall(
          "/api/job_seeker/v1/graphql",
          params,
          payload,
          {
            signal: controller.signal,
          }
        );

        clearTimeout(timeoutId); // clear timeout if completed in time
        const data = response?.data?.data?.internshipJobLists;
        const nodes = data?.nodes?.filter((job) => job !== null);
        if (response.status === 200 && nodes?.length > 0) {
          return dispatch({
            type: types.FETCH_RECOMMENDED_JOBS_SUCCEED,
            jobs: nodes,
            totalJobsCount: data.totalCount,
            totalPage: data.totalPage,
          });
        }

        return dispatch({ type: types.FETCH_RECOMMENDED_JOBS_FAILED });
      } catch (error) {
        if (error.name === "AbortError") {
          console.warn("Ashley recommended jobs API timed out.");
        } else {
          console.error("Ashley recommended jobs API failed:", error);
        }

        return dispatch({ type: types.FETCH_RECOMMENDED_JOBS_FAILED });
      }
    };

const getPostApplyRecommendedJobs =
  (params = {}) =>
    async (dispatch) => {
      dispatch({ type: types.FETCHING_RECOMMENDED_JOBS });

      const payload = {
        query: `{
        ashleySimilarRecommendedJobs(
          version: "v1.2", 
          globalId: "${params.globalId}", 
          first: ${params.first}, 
          internship: ${params.internship ?? false}
          ){
            maxPageSize
            totalCount
            totalPage
            nodes {
              ${jobListAttributes}
            }
          }
      }`,
      };

      // 🕒 Set timeout to abort after 5 seconds
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // abort after 5s

      try {
        const response = await api.apiCall(
          "/api/job_seeker/v1/graphql",
          params,
          payload,
          {
            signal: controller.signal,
          }
        );

        clearTimeout(timeoutId); // clear timeout if completed in time
        const data = response?.data?.data?.ashleySimilarRecommendedJobs;
        const nodes = data?.nodes?.filter((job) => job !== null);
        if (response.status === 200 && nodes?.length > 0) {
          return dispatch({
            type: types.FETCH_RECOMMENDED_JOBS_SUCCEED,
            jobs: nodes,
            totalJobsCount: data.totalCount,
            totalPage: data.totalPage,
          });
        }

        // Fallback if no results
        if (params.jobId) {
          return dispatch(
            getPostApplyRecommendedJobsTemp({ jobId: params.jobId })
          );
        }
        return dispatch({ type: types.FETCH_RECOMMENDED_JOBS_FAILED });
      } catch (error) {
        if (error.name === "AbortError") {
          console.warn("Ashley recommended jobs API timed out.");
        } else {
          console.error("Ashley recommended jobs API failed:", error);
        }

        // Fallback if fetch fails
        if (params.jobId) {
          return dispatch(
            getPostApplyRecommendedJobsTemp({ jobId: params.jobId })
          );
        }
        return dispatch({ type: types.FETCH_RECOMMENDED_JOBS_FAILED });
      }
    };

const getPostApplyRecommendedJobsWithoutRedux = (params = {}) => {
  const payload = {
    query: `{
      ashleySimilarRecommendedJobs(version: "v1.2", globalId: "${params.globalId}", first: ${params.first}) {
        maxPageSize
        totalCount
        totalPage
        nodes {
          ${jobListAttributes}
        }
      }
    }`,
  };

  return api
    .apiCall("/api/job_seeker/v1/graphql", params, payload)
    .then(async (response) => {
      if (response.status === 200) {
        if (response.data?.data?.ashleySimilarRecommendedJobs) {
          const jobs = response.data.data.ashleySimilarRecommendedJobs;
          const filteredJobs = jobs.nodes.filter((job) => job !== null);
          return filteredJobs;
        }
      }
      return [];
    });
};

const getPostApplyRecommendedJobsBoost =
  (params = {}) =>
    async (dispatch) => {
      dispatch({ type: types.FETCHING_RECOMMENDED_JOBS });

      const payload = {
        query: `{
        AshleySimilarRecommendedJobsWithBoost(version: "v1.2", globalId: "${params.globalId}") {
          maxPageSize
          totalCount
          totalPage
          nodes {
            ${jobListAttributes}
          }
        }
      }`,
      };

      return api
        .apiCall("/api/job_seeker/v1/graphql", params, payload)
        .then(async (response) => {
          if (response.status === 200) {
            if (response.data?.data?.AshleySimilarRecommendedJobsWithBoost) {
              const jobs =
                response.data.data.AshleySimilarRecommendedJobsWithBoost.nodes;
              return dispatch({
                type: types.FETCH_RECOMMENDED_JOBS_SUCCEED,
                jobs: jobs,
              });
            }
            return dispatch({
              type: types.FETCH_RECOMMENDED_JOBS_FAILED,
            });
          } else {
            return dispatch({
              type: types.FETCH_RECOMMENDED_JOBS_FAILED,
            });
          }
        });
    };

const getTrendingJobKeywords =
  (params = {}) =>
    async (dispatch) => {
      dispatch({ type: types.FETCHING_HOMEPAGE_TRENDING_KEYWORDS });

      let limitInput = `limit: ${params.limit ? params.limit : 10}`;

      const payload = {
        query: `{
        trendingKeywords(${limitInput}) {
          keywords
        }
      }`,
      };

      return api
        .apiCall("/api/job_seeker/v1/graphql", params, payload)
        .then(async (response) => {
          if (response.status === 200) {
            if (response.data?.data?.trendingKeywords?.keywords) {
              const keywords = response.data.data.trendingKeywords.keywords;
              return dispatch({
                type: types.FETCH_HOMEPAGE_TRENDING_KEYWORDS_SUCCEED,
                payload: keywords ? keywords : [],
              });
            }
            return dispatch({
              type: types.FETCH_HOMEPAGE_TRENDING_KEYWORDS_FAILED,
            });
          } else {
            return dispatch({
              type: types.FETCH_HOMEPAGE_TRENDING_KEYWORDS_FAILED,
            });
          }
        });
    };

const getTrendingJob = () => async () => {
  const payload = {
    query: `
    {
      trendingJob {
        data {
          id
          attributes {
            title
            trendingJob {
              keyword
              icon {
                data {
                  id
                  attributes {
                    url
                    alternativeText
                    name
                  }
                }
              }
            }
            locale
          }
        }
      }
    }    
    `,
  };

  try {
    const response = await strapiCall(payload);
    if (response.status !== 200) return {};
    const { attributes } = response?.data?.data?.trendingJob?.data || {};

    return attributes;
  } catch (error) {
    console.error("Error fetching trending job data:", error);
    return {};
  }
};

const updateJobAlertToggle = (status, syncToggle) => async (dispatch) => {
  return dispatch({
    type: types.UPDATE_JOB_ALERT_TOGGLE,
    status: status,
    syncToggle: syncToggle,
  });
};

const getJobAlerts =
  (params = {}) =>
    async (dispatch) => {
      dispatch({ type: types.FETCHING_JOB_ALERTS });

      const payload = {
        query: `{
        jobAlerts {
          totalNewJobsCount
          jobAlerts {
            newJobsCount
            jobAlert { 
            ${v1JobAlertAttributes}
            }
          }
        }
      }`,
      };

      let response;

      if (params.useFullUrl) {
        const apiUrl = setCountryApi(params);

        response = await api.apiCall(apiUrl, params, payload);
      } else {
        response = await api.apiCall(
          "/api/job_seeker/v1/graphql",
          params,
          payload
        );
      }

      if (response.status === 200) {
        if (response.data?.data?.jobAlerts?.jobAlerts) {
          const jobAlerts = response.data?.data?.jobAlerts?.jobAlerts.map(
            (item) => item.jobAlert
          );

          return dispatch({
            type: types.FETCH_JOB_ALERTS_SUCCEED,
            jobAlerts: jobAlerts ? jobAlerts : [],
          });
        }
        return dispatch({
          type: types.FETCH_JOB_ALERTS_FAILED,
        });
      } else {
        return dispatch({
          type: types.FETCH_JOB_ALERTS_FAILED,
        });
      }
    };

const getJobAlert =
  (params = {}) =>
    async (dispatch) => {
      const apiUrl = setCountryApi(params);

      dispatch({ type: types.GET_JOB_ALERT });

      const payload = {
        query: `{
      jobAlert(id: "${params.id}"){
        ${v1JobAlertAttributes}
      }
    }
  `,
      };

      return api.apiCall(apiUrl, params, payload).then(async (response) => {
        if (response.status === 200) {
          if (response.data?.data?.jobAlert) {
            const jobAlert = response.data.data.jobAlert;
            return dispatch({
              type: types.GET_JOB_ALERT_SUCCEED,
              jobAlert: jobAlert,
            });
          }
          return dispatch({
            type: types.GET_JOB_ALERT_FAILED,
          });
        } else {
          return dispatch({
            type: types.GET_JOB_ALERT_FAILED,
          });
        }
      });
    };

const createJobAlert =
  (params = {}) =>
    async (dispatch) => {
      dispatch({ type: types.CREATING_JOB_ALERT });

      let title = params.title ? `title: "${params.title}",` : "";
      let frequency = params.frequency ? `frequency: "${params.frequency}",` : "";
      let medium = params.medium ? `medium: "${params.medium}",` : "";
      let yearOfExperience =
        params.yearOfExperience && params.yearOfExperience.length > 0
          ? `yearOfExperience: [${params.yearOfExperience}],`
          : "";
      let salary = params.salary ? `salary: ${params.salary},` : "";

      const queryInput = title + frequency + medium + yearOfExperience + salary;

      const payload = {
        query: `
      mutation {
        addJobAlert(input: {${queryInput}}) {
          success
          errors {
            message
          }
          jobAlerts {
            newJobsCount
            jobAlert {
            ${v1JobAlertAttributes}
            }
          }
        }
      }
      `,
      };

      let response;

      if (params.useFullUrl) {
        const apiUrl = setCountryApi(params);

        response = await api.apiCall(apiUrl, params, payload);
      } else {
        response = await api.apiCall(
          "/api/job_seeker/v1/graphql",
          params,
          payload
        );
      }

      if (response.status === 200) {
        if (response.data?.data?.addJobAlert?.success) {
          let matchingJobAlert;

          const jobAlerts = response.data?.data?.addJobAlert?.jobAlerts.map(
            (item) => item.jobAlert
          );

          if (jobAlerts && Array.isArray(jobAlerts) && jobAlerts.length > 0) {
            const searchMatch = jobAlerts.filter(
              (alert) => alert.title === params.title
            );

            if (searchMatch.length > 0) {
              matchingJobAlert = searchMatch[0];
            }
          }

          return dispatch({
            type: types.CREATE_JOB_ALERT_SUCCEED,
            jobAlerts: jobAlerts ? jobAlerts : [],
            jobAlert: matchingJobAlert ?? {},
          });
        } else {
          const errors = response.data?.data?.addJobAlert?.errors;
          if (
            errors[0].message &&
            errors[0].message.includes(
              "you have reach maximum number of alerts per user!"
            )
          ) {
            return dispatch({
              type: types.REACHED_MAX_JOB_ALERTS,
            });
          }

          if (
            errors[0].message &&
            errors[0].message.includes("Keywords has already been added.")
          ) {
            return dispatch({
              type: types.JOB_ALERT_EXISTS,
            });
          }

          return dispatch({
            type: types.CREATE_JOB_ALERT_FAILED,
          });
        }
      }
      return await dispatch({
        type: types.CREATE_JOB_ALERT_FAILED,
      });
    };

const deleteJobAlert =
  (params = {}) =>
    async (dispatch) => {
      dispatch({ type: types.DELETING_JOB_ALERT });
      const payload = {
        query: `mutation {
        deleteJobAlert(input: {id: "${params.id}"}) {
          success
          errors {
            message
          }
          jobAlerts {
            ${v1JobAlertAttributes}
          }
        }
      }`,
      };

      let response;

      if (params.useFullUrl) {
        const apiUrl = setCountryApi(params);
        response = await api.apiCall(apiUrl, params, payload);
      } else {
        response = await api.apiCall(
          "/api/job_seeker/v1/graphql",
          params,
          payload
        );
      }

      if (response.status === 200) {
        if (response.data?.data?.deleteJobAlert) {
          const jobAlerts = response.data?.data?.deleteJobAlert?.jobAlerts;
          return dispatch({
            type: types.DELETE_JOB_ALERT_SUCCEED,
            jobAlerts: jobAlerts ? jobAlerts : [],
          });
        }
        return dispatch({
          type: types.DELETE_JOB_ALERT_FAILED,
        });
      } else {
        return dispatch({
          type: types.DELETE_JOB_ALERT_FAILED,
        });
      }
    };

const replaceJobAlert =
  (params = {}) =>
    (dispatch) => {
      dispatch({ type: types.REPLACING_JOB_ALERT });

      let queryInput = `job_alert_id: "${params.id}", title: "${params.title}"`;

      const payload = {
        query: `mutation {
        replaceJobAlert(input: {${queryInput}}) {
          success
          errors {
            message
          }
          job_alerts {
            ${jobAlertAttributes}
          }
        }
      }`,
      };

      return api
        .apiCall("/api/v2/graphql", params, payload)
        .then(async (response) => {
          if (response.status === 200) {
            if (response.data?.data?.replaceJobAlert) {
              const jobAlerts = response.data.data.replaceJobAlert.job_alerts;
              return dispatch({
                type: types.REPLACE_JOB_ALERT_SUCCEED,
                jobAlerts: jobAlerts ? jobAlerts : [],
              });
            }
            return dispatch({
              type: types.REPLACE_JOB_ALERT_FAILED,
            });
          } else {
            return dispatch({
              type: types.REPLACE_JOB_ALERT_FAILED,
            });
          }
        });
    };

const updateJobAlert =
  (params = {}) =>
    async (dispatch) => {
      dispatch({ type: types.UPDATING_JOB_ALERT });

      const currentDate = dayjs().format("YYYY-MM-DD");
      let keywordInput = `title: "${params.keyword}"`;
      let frequencyInput = `frequency: "${params.frequency}",`;
      let mediumInput = `medium: "${params.medium}",`;
      let yearOfExperience = `yearOfExperience: [${params.yearOfExperience}],`;
      let salary = `salary: ${params.salary},`;
      let activate = `activate : ${params.activate},`;
      let lastAccessAtInput = `lastAccessAt: "${currentDate}",`;
      let queryInput =
        `id: ${params.id},` +
        keywordInput +
        frequencyInput +
        mediumInput +
        yearOfExperience +
        salary +
        activate +
        lastAccessAtInput;

      // Remove trailing ","
      if (queryInput.charAt(queryInput.length - 1) == ",") {
        queryInput = queryInput.substring(0, queryInput.length - 1);
      }

      const payload = {
        query: `mutation {
        updateJobAlert(input: {${queryInput}}) {
          success
          errors {
            message
          }
          jobAlert {
            newJobsCount
            jobAlert {
              ${v1JobAlertAttributes}
            }
          }
        }
      }
      `,
      };

      let response;

      if (params.useFullUrl) {
        const apiUrl = setCountryApi(params);
        response = await api.apiCall(apiUrl, params, payload);
      } else {
        response = await api.apiCall(
          "/api/job_seeker/v1/graphql",
          params,
          payload
        );
      }

      if (response.status === 200) {
        if (response.data?.data?.updateJobAlert) {
          const jobAlert = response.data.data.updateJobAlert.jobAlert.jobAlert;
          return dispatch({
            type: types.UPDATE_JOB_ALERT_SUCCEED,
            jobAlert: jobAlert,
          });
        }
        return dispatch({
          type: types.UPDATE_JOB_ALERT_FAILED,
        });
      } else {
        return dispatch({
          type: types.UPDATE_JOB_ALERT_FAILED,
        });
      }
    };

const disableAllJobAlerts =
  (params = {}) =>
    async (dispatch) => {
      dispatch({ type: types.DISABLING_ALL_JOB_ALERTS });

      const disableFlag =
        typeof params.disableAllJobAlerts === "boolean"
          ? params.disableAllJobAlerts
          : true;

      const payload = {
        query: `
        mutation {
          disableAllJobAlerts(input: { disableAllJobAlerts: ${disableFlag} }) {
            success
            errors { message }
          }
        }
      `,
      };

      let response;

      if (params.useFullUrl) {
        const apiUrl = setCountryApi(params);
        response = await api.apiCall(apiUrl, params, payload);
      } else {
        response = await api.apiCall(
          "/api/job_seeker/v1/graphql",
          params,
          payload
        );
      }

      if (response.status === 200) {
        if (response.data?.data?.disableAllJobAlerts) {
          return dispatch({
            type: types.DISABLE_ALL_JOB_ALERTS_SUCCEED,
          });
        }
        return dispatch({
          type: types.DISABLE_ALL_JOB_ALERTS_FAILED,
        });
      } else {
        return dispatch({
          type: types.DISABLE_ALL_JOB_ALERTS_FAILED,
        });
      }
    };

const visitorAddJobAlert =
  (params = {}) =>
    async (dispatch) => {
      dispatch({ type: types.VISITOR_ADDING_JOB_ALERT }); //Before modification: Email and title only
      let queryInput = `email: "${params.email}", title: "${params.title}", yearOfExperience: [${params.yearOfExperience}], salary: ${params.salary}`;

      const payload = {
        query: `mutation {
        visitorAddJobAlert(input: {${queryInput}}) {
          success
          errors {
            message
          }
          maxJobAlerts
          jobAlert {
            ${v1JobAlertAttributes}
          }
        }
      }
    `,
      };

      return api
        .apiCall("/api/job_seeker/v1/graphql", params, payload)
        .then(async (response) => {
          if (response.status === 200) {
            if (response.data?.data?.visitorAddJobAlert?.success) {
              if (params.origin) {
                // Custom event for visitor job alert creation on job alert landing page
                sendTrackingEvent({
                  event: "CE_create-job-alert-job-alert-landing-page",
                  "job-alert-id":
                    response.data?.data?.visitorAddJobAlert?.jobAlert?.id,
                  "job-alert-frequency":
                    response.data?.data?.visitorAddJobAlert?.jobAlert?.frequency,
                  "job-alert-medium":
                    response.data?.data?.visitorAddJobAlert?.jobAlert?.medium,
                });
              }

              return dispatch({
                type: types.VISITOR_ADD_JOB_ALERT_SUCCEED,
                jobAlert: response.data?.data?.visitorAddJobAlert?.jobAlert,
              });
            }

            if (response.data?.data?.visitorAddJobAlert?.maxJobAlerts) {
              return dispatch({
                type: types.VISITOR_ADD_JOB_ALERT_FAILED,
                error: "You have reach maximum number of alerts per visitor",
              });
            }

            if (
              response.data?.data?.visitorAddJobAlert?.errors &&
              response.data?.data?.visitorAddJobAlert?.errors?.length > 0
            ) {
              // If user account already exists or email exists
              if (response.data?.data?.visitorAddJobAlert?.errors[0]?.message) {
                return dispatch({
                  type: types.VISITOR_ADD_JOB_ALERT_FAILED,
                  error:
                    response.data?.data?.visitorAddJobAlert?.errors[0]?.message,
                });
              }
            }

            if (
              response.data?.errors &&
              Array.isArray(response.data?.errors) &&
              response.data?.errors?.length > 0
            ) {
              return dispatch({
                type: types.VISITOR_ADD_JOB_ALERT_FAILED,
                error:
                  response.data?.errors[0]?.message ??
                  "Oops! Something went wrong. Please try again later",
              });
            }
          } else {
            return dispatch({
              type: types.VISITOR_ADD_JOB_ALERT_FAILED,
              error: "Oops! Something went wrong. Please try again later",
            });
          }
        });
    };

const visitorUnsubscribeJobAlert =
  (params = {}) =>
    (dispatch) => {
      dispatch({ type: types.VISITOR_UNSUBSCRIBING_JOB_ALERT });

      let queryInput = `id: ${params.id}, reason: "${params.feedback}"`;

      const payload = {
        query: `mutation {
        visitorUnsubscribeJobAlert(input: {${queryInput}}) {
          success
          errors {
              message
          }
        }
      }`,
      };

      return api
        .apiCall("/api/v2/graphql", params, payload)
        .then(async (response) => {
          if (response.status == 200) {
            if (response.data?.data?.visitorUnsubscribeJobAlert?.success) {
              return dispatch({
                type: types.VISITOR_UNSUBSCRIBE_JOB_ALERT_SUCCEED,
              });
            }
            return dispatch({
              type: types.VISITOR_UNSUBSCRIBE_JOB_ALERT_FAILED,
            });
          }
          return dispatch({
            type: types.VISITOR_UNSUBSCRIBE_JOB_ALERT_FAILED,
          });
        });
    };

const clearJobAlerts = () => async (dispatch) => {
  return new Promise((resolve) => {
    return resolve(
      dispatch({
        type: types.CLEAR_JOB_ALERTS,
      })
    );
  });
};

const fetchSearchSuggestions =
  (params = {}) =>
    async (dispatch) => {
      dispatch({ type: types.FETCHING_SEARCH_DATA });
      const escapedKeyword = escapeDoubleQuoteFromPayload(params.keyword || "");
      const payload = {
        query: `{
        autoSuggestTitles(type: "job", keyword: "${escapedKeyword}") {
          data
          suggestions
        }
      }      
      `,
      };

      return api
        .apiCall("/api/job_seeker/v1/graphql", params, payload)
        .then(async (response) => {
          if (response.status === 200) {
            if (response.data?.data?.autoSuggestTitles) {
              const suggestedSearch = response.data.data.autoSuggestTitles;
              if (suggestedSearch) {
                return dispatch({
                  type: types.FETCH_SEARCH_DATA_SUCCEED,
                  payload: {
                    searchData: suggestedSearch.data ? suggestedSearch.data : [],
                    searchSuggestions: suggestedSearch.suggestions
                      ? suggestedSearch.suggestions
                      : [],
                  },
                });
              }
            }
            return dispatch({
              type: types.FETCH_SEARCH_DATA_FAILED,
            });
          }
          return dispatch({
            type: types.FETCH_SEARCH_DATA_FAILED,
          });
        });
    };

const updateSearchType = (params) => async (dispatch) => {
  return dispatch({
    type: types.UPDATE_SEARCH_TYPE,
    payload: params,
  });
};

const clearSearchSuggestions = () => async (dispatch) => {
  return new Promise((resolve) => {
    return resolve(
      dispatch({
        type: types.CLEAR_SEARCH_SUGGESTIONS,
      })
    );
  });
};

const unSaveJob = (isSignedIn, jobId, origin) => async (dispatch) => {
  return new Promise((resolve) => {
    if (!isSignedIn) return;

    if (jobId) {
      store.getState().jobs.currentId = jobId;

      let params = {
        jobId: jobId,
        canCancel: false,
        origin: origin ? "recommended-jobs" : "individual-job",
      };

      dispatch(unBookmarkJob(params));
    }

    return resolve(
      dispatch({
        type: types.UNSAVE_JOB,
      })
    );
  });
};

const saveJob = (isSignedIn, jobId, origin) => async (dispatch) => {
  return new Promise((resolve) => {
    if (!isSignedIn) {
      dispatch(updateSignInText("Log In to Save Jobs"));
      dispatch(updateSignUpText("Sign Up to Save Jobs"));
      return;
    }

    if (jobId) {
      store.getState().jobs.currentId = jobId;

      let params = {
        jobId: jobId,
        canCancel: false,
        origin: origin ? "recommended-jobs" : "individual-job",
      };

      dispatch(bookmarkJob(params));
    }

    return resolve(
      dispatch({
        type: types.SAVE_JOB,
      })
    );
  });
};

const updateAshleyInfoDialog = (params) => async (dispatch) => {
  return dispatch({
    type: types.UPDATE_ASHLEY_TOGGLE_DIALOG,
    payload: params,
  });
};

const getAshleyRecommendedJobs =
  (params = {}) =>
    async (dispatch) => {
      dispatch({ type: types.FETCHING_ASHLEY_RECOMMENDED_JOBS });
      /**
       * ASHLEY RECOMMENDATION BEHAVIOUR
       *
       * Scenario 1 - going to NEXT page
       * {last: null, first: null, before: "null", after: "endCursor" }
       *
       * Scenario 2 - going to PREVIOUS page
       * {last: 30, first: null, before: "startCursor", after: "" }
       *
       * NOTE: after needs to be an empty string when going to previous page for it to work properly
       */

      const localEndCursor = params.endCursor ?? "";
      const localStartCursor = params.startCursor ?? "";

      const payload = {
        query: `{
        ashleyRecommendedJobs(last: ${params.last}, first: ${params.first} before: "${localStartCursor}", after: "${localEndCursor}", version:"v1.2") {
          totalCount
          totalPage
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
          nodes {
            ${jobListAttributes}
          }
        }     
      }
    `,
      };

      // 🕒 Set timeout to abort after 5 seconds
      const controller = new AbortController();
      const timeout = setTimeout(() => {
        controller.abort();
      }, 5000); // abort after 5s

      try {
        const response = await api.apiCall(
          "/api/job_seeker/v1/graphql",
          { ...params, signal: controller.signal },
          payload
        );

        clearTimeout(timeout);

        if (response.status === 200) {
          const { errors } = response.data;
          const errorObj = Array.isArray(errors) ? errors[0] : errors;
          const { error_code } = errorObj?.extensions || {};

          if (
            (errors !== undefined &&
              errors !== null &&
              Array.isArray(errors) &&
              errors?.length > 0) ||
            (error_code !== undefined && error_code !== null)
          ) {
            return dispatch({
              type: types.FETCH_ASHLEY_RECOMMENDED_JOBS_FAILED,
              errorMessage: Array.isArray(errors) ? errors[0]?.message : "Error",
              currentLoadingJobsKey: params.currentLoadingJobsKey,
              ashleyRecommendsErrorCode: error_code ?? "",
            });
          }

          const jobList = response.data.data.ashleyRecommendedJobs.nodes;
          const filteredJobs = jobList.filter((job) => job !== null);
          const totalPages = response.data.data.ashleyRecommendedJobs.totalPage;
          const totalJobsCount =
            response.data.data.ashleyRecommendedJobs.totalCount;
          const pageInfo = response.data.data.ashleyRecommendedJobs.pageInfo;

          if (filteredJobs?.length === 0) {
            console.warn("Ashley returned 0 jobs, falling back.");
            return dispatch(fetchAshleyBackupJobs(params));
          }

          return dispatch({
            type: types.FETCH_ASHLEY_RECOMMENDED_JOBS_SUCCEED,
            payload: {
              filteredJobs,
              refresh: params.refresh,
              currentLoadingJobsKey: params.currentLoadingJobsKey,
              totalJobsPages: totalPages,
              totalJobsCount: totalJobsCount,
              ashleyRecommendsError: false,
              pageInfo: pageInfo,
            },
          });
        } else {
          clearTimeout(timeout);
          return dispatch(fetchAshleyBackupJobs(params));
        }
      } catch (error) {
        clearTimeout(timeout);

        if (error.name === "AbortError") {
          console.warn("Ashley API call timed out. Falling back to backup jobs.");
        } else {
          console.error(
            "Ashley API call failed. Falling back to backup jobs.",
            error
          );
        }

        return dispatch(fetchAshleyBackupJobs(params));
      }
    };

const getAshleyAwsRecommendedJobs =
  (params = {}) =>
    async (dispatch) => {
      dispatch({ type: types.FETCHING_ASHLEY_AWS_RECOMMENDED_JOBS });
      /**
       * ASHLEY RECOMMENDATION BEHAVIOUR
       *
       * Scenario 1 - going to NEXT page
       * {last: null, first: null, before: "null", after: "endCursor" }
       *
       * Scenario 2 - going to PREVIOUS page
       * {last: 30, first: null, before: "startCursor", after: "" }
       *
       * NOTE: after needs to be an empty string when going to previous page for it to work properly
       */

      const localEndCursor = params.endCursor ?? "";
      const localStartCursor = params.startCursor ?? "";

      const payload = {
        query: `{
        ashleyAwsPersonalizeRecommendedJobs(last: ${params.last}, first: ${params.first} before: "${localStartCursor}", after: "${localEndCursor}") {
        totalCount
        totalPage
        pageInfo {
          hasNextPage
          hasPreviousPage
          startCursor
          endCursor
        }
        nodes {
          ${jobListAttributes}
        }
      }     
    }
  `,
      };

      return api
        .apiCall("/api/job_seeker/v1/graphql", params, payload)
        .then(async (response) => {
          if (response.status === 200) {
            const { errors } = response.data;

            const errorObj = Array.isArray(errors) ? errors[0] : errors;

            const { error_code } = errorObj?.extensions || {};

            if (
              (errors !== undefined &&
                errors !== null &&
                Array.isArray(errors) &&
                errors?.length > 0) ||
              (error_code !== undefined && error_code !== null)
            ) {
              return dispatch({
                type: types.FETCH_ASHLEY_RECOMMENDED_JOBS_FAILED,
                errorMessage: Array.isArray(errors)
                  ? errors[0]?.message
                  : "Error",
                currentLoadingJobsKey: params.currentLoadingJobsKey,
                ashleyRecommendsErrorCode: error_code ?? "",
              });
            }

            const pageInfo =
              response.data.data.ashleyAwsPersonalizeRecommendedJobs.pageInfo;

            const jobList =
              response.data.data.ashleyAwsPersonalizeRecommendedJobs.nodes;
            const totalPages =
              response.data.data.ashleyAwsPersonalizeRecommendedJobs.totalPage;
            const totalJobsCount =
              response.data.data.ashleyAwsPersonalizeRecommendedJobs.totalCount;
            const filteredJobs = jobList.filter((job) => job !== null);

            return dispatch({
              type: types.FETCH_ASHLEY_AWS_RECOMMENDED_JOBS_SUCCEED,
              payload: {
                jobList,
                filteredJobs,
                refresh: params.refresh,
                currentLoadingJobsKey: params.currentLoadingJobsKey,
                totalJobsPages: totalPages,
                totalJobsCount: totalJobsCount,
                ashleyRecommendsError: false,
                pageInfo: pageInfo,
              },
            });
          } else {
            return dispatch({
              type: types.FETCH_ASHLEY_AWS_RECOMMENDED_JOBS_FAILED,
              currentLoadingJobsKey: params.currentLoadingJobsKey,
            });
          }
        })
        .catch(() => {
          return dispatch({
            type: types.FETCH_ASHLEY_AWS_RECOMMENDED_JOBS_FAILED,
            currentLoadingJobsKey: params.currentLoadingJobsKey,
          });
        });
    };

const closeAshleyErrorDialog = () => async (dispatch) => {
  return dispatch({
    type: types.CLOSE_ASHLEY_ERROR_DIALOG,
  });
};

const getHomepageLogo =
  (params = {}) =>
    async (dispatch) => {
      const payload = {
        query: `{
        homepageFeaturedCompanyLogo {
          totalCount
          pageInfo {
            startCursor
            hasNextPage
            hasPreviousPage
            endCursor
          }
          edges {
            node {
              companyId
              companyName
              company{
                slug
              }
              logo
              order
            }
          }
        }
      }  
    `,
      };

      return api
        .apiCall("/api/job_seeker/v1/graphql", params, payload)
        .then(async (response) => {
          if (response.status === 200) {
            const { edges } = response?.data?.data?.homepageFeaturedCompanyLogo;
            return dispatch({
              type: types.FETCH_HOMEPAGE_LOGO,
              payload: edges,
            });
          }
        });
    };

const getTrendingChip =
  (params = {}) =>
    async (dispatch, getState) => {
      let limitInput = `first: ${params.first ? params.first : 10}`;

      try {
        const payload = {
          query: `{
          homepageTrendingSearches(${limitInput}) {
            nodes{
              id
              order
              title
            }
          }
        }`,
        };

        const response = await api.apiCall(
          "/api/job_seeker/v1/graphql",
          params,
          payload
        );

        if (response.status === 200) {
          const { nodes } = response?.data?.data?.homepageTrendingSearches || [];

          if (nodes) {
            return dispatch({
              type: types.FETCH_TRENDING_CHIP,
              payload: nodes,
            });
          }
        }
      } catch (error) {
        // Error handling
      }
    };

const getHomepageAds =
  (params = {}) =>
    async (dispatch) => {
      const payload = {
        query: `{
        homepageSliders {
          totalCount
          pageInfo {
            startCursor
            hasNextPage
            hasPreviousPage
            endCursor
          }
          edges {
            node {
              title
              description
              chipText
              image
              urlLink
              order
            }
          }
        }
      }  
    `,
      };

      return api
        .apiCall("/api/job_seeker/v1/graphql", params, payload)
        .then(async (response) => {
          if (response.status === 200) {
            const { edges } = response.data.data.homepageSliders;
            return dispatch({
              type: types.FETCH_HOMEPAGE_ADS,
              payload: edges,
            });
          }
        });
    };

const updateJobAlertEmail = (params) => async (dispatch) => {
  return dispatch({
    type: types.UPDATE_JOB_ALERT_EMAIL,
    payload: params,
  });
};

const updateShowJobAlertDialog = (params) => async (dispatch) => {
  return dispatch({
    type: types.SHOW_VISITOR_JOB_ALERT_DIALOG,
    payload: params,
  });
};
const updateShowJobAlertSuccessDialog = (params) => async (dispatch) => {
  return dispatch({
    type: types.SHOW_VISITOR_JOB_ALERT_SUCCESS,
    payload: params,
  });
};

const updateForYouPaginationCounter = (params) => async (dispatch) => {
  return dispatch({
    type: types.UPDATE_FOR_YOU_PAGINATION_COUNT,
    counter: params,
  });
};

const updateSelectedJobId = (params) => async (dispatch) => {
  return dispatch({
    type: types.SELECTED_JOB_ID,
    payload: params,
  });
};

const updateFetchingForYou = (status) => async (dispatch) => {
  return dispatch({
    type: types.FETCHING_FOR_YOU,
    status: status,
  });
};

const getRecentlyViewedJobs =
  (params = {}) =>
    async (dispatch) => {
      dispatch({ type: types.FETCHING_RECENT_VIEWED_JOBS });

      const localEndCursor = params.endCursor ?? "";
      const localStartCursor = params.startCursor ?? "";

      const payload = {
        query: `{
        recentlyViewedJobs(last: ${params.last}, first: ${params.first}, before: "${localStartCursor}", after: "${localEndCursor}") {
          totalCount
          pageInfo {
            startCursor
            hasNextPage
            hasPreviousPage
            endCursor
          }
          nodes {
            ${jobListAttributes}
          }
        }
      }
      `,
      };

      return api
        .apiCall("/api/job_seeker/v1/graphql", params, payload)
        .then(async (response) => {
          if (response.status === 200) {
            const { errors } = response.data;

            if (Array.isArray(errors) && errors.length > 0) {
              return dispatch({
                type: types.FETCH_RECENT_VIEWED_JOBS_FAILED,
                currentLoadingJobsKey: params.currentLoadingJobsKey,
              });
            }

            const pageInfo = response.data?.data?.recentlyViewedJobs?.pageInfo;
            const jobList = response.data?.data?.recentlyViewedJobs?.nodes;

            return dispatch({
              type: types.FETCH_RECENT_VIEWED_JOBS_SUCCEED,
              payload: {
                jobList: jobList,
                pageInfo: pageInfo,
                historyExist: jobList?.length > 0,
              },
            });
          } else {
            return dispatch({
              type: types.FETCH_RECENT_VIEWED_JOBS_FAILED,
              currentLoadingJobsKey: params.currentLoadingJobsKey,
            });
          }
        })
        .catch(() => {
          return dispatch({
            type: types.FETCH_RECENT_VIEWED_JOBS_FAILED,
            currentLoadingJobsKey: params.currentLoadingJobsKey,
          });
        });
    };

const postGaSessionId =
  (params = {}) =>
    async () => {
      const payload = {
        query: `{
        job(id: "${params.jobId}", sessionId: "${params.sessionId}"){
          id
        }
      }
      `,
      };

      const result = api.apiCall("/api/job_seeker/v1/graphql", params, payload);

      if (result.status === 200 && result?.data?.data?.job?.id) return true;

      return false;
    };

const getPostApplyRecommendedJobsTemp =
  (params = {}) =>
    async (dispatch) => {
      const payload = {
        query: `{
        similarJobsUnderJobPage(jobId: "${params.jobId}", first: 9) {
        maxPageSize
        totalCount
        totalPage
          nodes {
            ${jobListAttributes}
          }
        }
      }
    `,
      };

      return api
        .apiCall("/api/job_seeker/v1/graphql", params, payload)
        .then(async (response) => {
          if (response.status === 200) {
            if (response.data?.data?.similarJobsUnderJobPage) {
              const jobs = response.data.data.similarJobsUnderJobPage.nodes;
              const filteredJobs = jobs.filter((job) => job !== null);
              const totalJobsCount =
                response.data.data.similarJobsUnderJobPage.totalCount;
              const totalPage =
                response.data.data.similarJobsUnderJobPage.totalPage;

              return dispatch({
                type: types.FETCH_RECOMMENDED_JOBS_SUCCEED,
                jobs: filteredJobs,
                totalJobsCount: totalJobsCount,
                totalPage: totalPage,
              });
            }
            return dispatch({
              type: types.FETCH_RECOMMENDED_JOBS_FAILED,
            });
          } else {
            return dispatch({
              type: types.FETCH_RECOMMENDED_JOBS_FAILED,
            });
          }
        });
    };

const getJobSimilarJobsV1 =
  (params = {}) =>
    async (dispatch) => {
      const payload = {
        query: `{
          similarJobsUnderJobPage(jobId: "${params.jobId}", first: 6) {
            nodes {
              ${jobListAttributes}
            }
          }
        }
      `,
      };

      try {
        const result = await api.apiCall(
          "/api/job_seeker/v1/graphql",
          params,
          payload
        );

        if (
          Array.isArray(result.data?.errors) &&
          result.data?.errors?.length > 0
        ) {
          return dispatch({
            type: types.FETCH_SIMILAR_JOBS_UNDER_JOB_PAGE_FAILED,
          });
        }

        if (result.status === 200) {
          const { nodes } = result?.data?.data?.similarJobsUnderJobPage || {};

          return dispatch({
            type: types.FETCH_SIMILAR_JOBS_UNDER_JOB_PAGE_SUCCEED,
            payload: nodes,
          });
        }

        return dispatch({
          type: types.FETCH_SIMILAR_JOBS_UNDER_JOB_PAGE_FAILED,
        });
      } catch (error) {
        return dispatch({
          type: types.FETCH_SIMILAR_JOBS_UNDER_JOB_PAGE_FAILED,
        });
      }
    };

const bookmarkSingleJobCompany =
  (params = {}) =>
    async (dispatch) => {
      const payload = {
        query: `
      mutation {
        bookmarkCompany(input: {companyId: "${params.companyId}"}) {
          bookmarkCompany {
            company {
              bookmark
            }
          }
        }
      }    
    `,
      };

      const response = await api.apiCall(
        "/api/job_seeker/v1/graphql",
        params,
        payload
      );

      if (response.status !== 200)
        return dispatch({ type: types.FOLLOW_SINGLE_JOB_COMPANY_FAILED });

      const { bookmark } =
        response?.data?.data?.bookmarkCompany?.bookmarkCompany?.company || {};

      if (bookmark !== null && bookmark !== undefined) {
        return dispatch({
          type: types.FOLLOW_SINGLE_JOB_COMPANY_SUCCEED,
          payload: {
            newBookmarkStatus: bookmark,
          },
        });
      } else {
        const { errors } = response.data || {};

        return dispatch({
          type: types.FOLLOW_SINGLE_JOB_COMPANY_FAILED,
          message: Array.isArray(errors) ? errors[0].toString() : null,
        });
      }
    };

const unBookmarkSingleJobCompany =
  (params = {}) =>
    async (dispatch) => {
      const payload = {
        query: `
      mutation {
        unBookmarkCompany(input: {companyId: "${params.companyId}"}) {
          company {
            bookmark
          }          
        }
      }       
    `,
      };

      const response = await api.apiCall(
        "/api/job_seeker/v1/graphql",
        params,
        payload
      );

      if (response.status !== 200)
        return dispatch({ type: types.FOLLOW_SINGLE_JOB_COMPANY_FAILED });

      const { bookmark } = response?.data?.data?.unBookmarkCompany?.company || {};

      if (bookmark !== null && bookmark !== undefined) {
        return dispatch({
          type: types.FOLLOW_SINGLE_JOB_COMPANY_SUCCEED,
          payload: {
            newBookmarkStatus: bookmark,
          },
        });
      } else {
        const { errors } = response.data || {};

        return dispatch({
          type: types.FOLLOW_SINGLE_JOB_COMPANY_FAILED,
          message: Array.isArray(errors) ? errors[0].toString() : null,
        });
      }
    };

// To manage predefined job filter flow
const updateCheckingUrlParamForJobFilterStatus =
  (status) => async (dispatch) => {
    return new Promise((resolve) => {
      return resolve(
        dispatch({
          type: types.UPDATE_CHECKING_URL_PARAM_FOR_JOB_FILTER_STATUS,
          status: status,
        })
      );
    });
  };

const getJobsMicrosite =
  (params = {}) =>
    async (dispatch) => {
      dispatch({ type: types.FETCHING_JOBS });

      const stateRegionInput = params?.stateRegionList
        ? `stateRegionList:[${params?.stateRegionList.map(
          (item) => `"${item}"`
        )}]`
        : `stateRegionList:null`;

      const trackListInput = params?.trackIdList
        ? `trackIdList:[${params?.trackIdList}]`
        : `trackIdList:null`;

      const payload = {
        query: `{
        micrositeForJobs(companyName: "${params?.companyName}", first:${params?.first}, last:${params?.last}, after:"${params?.after}", before:"${params?.before}", ${stateRegionInput}, ${trackListInput}, companyIndustriesIdList:${params?.companyIndustriesIdList}, keyword:"${params?.keyword}",) {
          nodes {
            title
            company {
              id
              name
              logo
              isMagic
              isMdec
            }
            jobType
            location
            stateRegion
            imageThumb
            tracks {
              title
            }
            slug
          }
          pageInfo {
            endCursor
            hasNextPage
          }
        }
      }    
    `,
      };

      return api
        .apiCall("/api/job_seeker/v1/graphql", params, payload)
        .then(async (response) => {
          if (response.status === 200) {
            if (response?.data?.data?.micrositeForJobs) {
              const jobList = response?.data?.data?.micrositeForJobs?.nodes;
              const pageInfo = response.data?.data?.micrositeForJobs?.pageInfo;

              return dispatch({
                type: types.FETCH_MICROSITE_JOBS_SUCCEED,
                payload: {
                  jobList: jobList,
                  pageInfo: pageInfo,
                },
              });
            }
          }
          return dispatch({
            type: types.FETCH_MICROSITE_JOBS_FAILED,
          });
        });
    };

const updateJobListSearchKeyword = (params) => async (dispatch) => {
  dispatch(
    updateSearchFeedback({
      showCard: true,
      feedbackSubmit: false,
    })
  );
  return dispatch({
    type: types.UPDATE_JOB_SEARCH_KEYWORD,
    payload: params.keyword,
  });
};

const updateJobSearchKeyword = (params) => async (dispatch) => {
  dispatch(
    updateSearchFeedback({
      showCard: true,
      feedbackSubmit: false,
    })
  );

  const cleanKeyword = params?.keyword?.replace(/[-_]/g, " ");

  return dispatch({
    type: types.UPDATE_JOB_SEARCH_OPTION_KEYWORD,
    payload: cleanKeyword,
  });
};

const updateSearchFeedback = (params) => async (dispatch) => {
  return dispatch({
    type: types.UPDATE_SEARCH_FEEDBACK,
    payload: params,
  });
};

const getJobsCampaign =
  (params = {}) =>
    async (dispatch) => {
      dispatch({ type: types.FETCH_CAMPAIGN_JOBS });

      const firstParams = params.first ? "first: " + params.first : "null";
      const lastParams = params.last ? ", last: " + params.last : "";
      const afterParams = params.after ? ", after: " + `"${params.after}"` : "";
      const beforeParams = params.before
        ? ", before: " + `"${params.before}"`
        : "";

      const queryParams = firstParams + lastParams + afterParams + beforeParams;

      const campaignAttributes = jobListAttributes
        .replace(/\bgptSummary\b\n?/g, "")
        .trim();

      const payload = {
        query: `{
        marketingCampaign(slug:"${params.slug}") {
          campaignDescription
          campaignName
          id
          imageUrl
          slug
          jobs(${queryParams}) {
            pageInfo{
              startCursor
              endCursor
              hasNextPage
              hasPreviousPage
            }
            nodes {
            ${campaignAttributes}
          }
        }
      }
    }
  `,
      };

      return api
        .apiCall("/api/job_seeker/v1/graphql", params, payload, true)
        .then(async (response) => {
          if (response.status === 200) {
            if (response?.data?.data?.marketingCampaign) {
              const campaignObj = response?.data?.data?.marketingCampaign;

              const campaign = {
                campaignDescription: campaignObj.campaignDescription,
                campaignName: campaignObj.campaignName,
                id: campaignObj.id,
                imageUrl: campaignObj.imageUrl,
                slug: campaignObj.slug,
              };

              const pageInfo = campaignObj?.jobs?.pageInfo;
              const jobs = campaignObj?.jobs?.nodes;

              return dispatch({
                type: types.FETCH_CAMPAIGN_JOBS_SUCCEED,
                payload: {
                  campaign: campaign,
                  pageInfo: pageInfo,
                  jobs: jobs,
                },
              });
            }
          }
          return dispatch({
            type: types.FETCH_CAMPAIGN_JOBS_FAILED,
          });
        });
    };

const getFooterCompanyWriteup = async (params = {}) => {
  const normalizeStrapiLocale = (language) => {
    const normalizedLanguage = language
      ?.toString()
      ?.toLowerCase()
      ?.split("-")[0];
    return normalizedLanguage === "ms" ? "ms" : "en";
  };
  const requestedLocale = params?.locale;
  const normalizedLocale = normalizeStrapiLocale(requestedLocale);
  const localeCandidates = Array.from(
    new Set([requestedLocale, normalizedLocale].filter(Boolean))
  );

  const buildLocalizedPayload = (locale) => ({
    // query FooterWriteup($locale: I18NLocaleCode) {
    //   footerWriteup(locale: $locale) {
    query: `
    query FooterWriteup {
      footerWriteup {
        data {
          attributes {
            title
            content
          }
        }
      }
    }    
    `,
    // variables: {
    //   locale,
    // },
  });

  const legacyPayload = {
    query: `
    {
      footerWriteup {
        data {
          attributes {
            title
            content
          }
        }
      }
    }    
    `,
  };

  for (const locale of localeCandidates) {
    const response = await strapiCall(buildLocalizedPayload(locale));
    const hasGraphQLError =
      Array.isArray(response?.data?.errors) && response.data.errors.length > 0;
    const localizedData = response?.data?.data?.footerWriteup?.data;

    if (response.status === 200 && localizedData && !hasGraphQLError) {
      return localizedData;
    }
  }

  // Fallback for environments where footerWriteup is not localized in Strapi yet.
  const legacyResponse = await strapiCall(legacyPayload);

  if (legacyResponse.status === 200) {
    const { data } = legacyResponse?.data?.data?.footerWriteup || {};

    return data ?? null;
  } else {
    return null;
  }
};

const getCmsSpecialisations = async (params = {}) => {
  const payload = {
    query: `
    {
      specialisations {
        data {
          id
          attributes {
            name
            slug
            writeup
          }
        }
      }
    }    
    `,
  };

  const response = await strapiCall(payload);

  if (response.status === 200) {
    const { data } = response?.data?.data?.specialisations || {};

    return data ?? null;
  } else {
    return null;
  }
};

const fetchInternshipBannerContent = (query) => async (dispatch) => {
  const payload = {
    query: `
    {
      internshipBankPage {
        data {
          attributes {
            ${query}
          }
        }
      }
    }
    `,
  };

  const response = await strapiCall(payload);

  if (response.status === 200) {
    const qualities =
      response?.data?.data?.internshipBankPage?.data?.attributes;
    return qualities ?? {};
  }
  return {};
};

const getCmsLocations = async (params = {}) => {
  const payload = {
    query: `
    {
      stateRegions(filters: { country: { eqi: "${process.env.NEXT_PUBLIC_JSW_GEOLOCATION}"  } }, sort: ["id"]) {
        data {
          attributes {
            location
            slug
          }
        }
      }
    }    
    `,
  };

  const response = await strapiCall(payload);

  if (response.status === 200) {
    const { data } = response?.data?.data?.stateRegions || {};

    return data ?? null;
  } else {
    return null;
  }
};

const getCmsSubSpecialisation = async (params = {}) => {
  const payload = {
    query: `
    {
      subSpecialisations(filters: { slug: { eqi: "${params.keyword}" } }) {
        data {
          attributes {
            name
            writeup
          }
        }
      }
    }    
    `,
  };

  const response = await strapiCall(payload);

  if (response.status === 200) {
    const { data } = response?.data?.data?.subSpecialisations || {};

    return Array.isArray(data) && data.length > 0 ? data[0] : null;
  }
};

const getCmsSitemap = async (params = {}) => {
  const sitemapFilter = params?.sitemapFilter || "sitemap-1-filter";

  const payload = {
    query: `
      query {
        seoContainers(
          filters: { sitemapLevel: { eq: "${sitemapFilter}" } }
          pagination: { limit: 10000 }
        ) {
          data {
            id
            attributes {
              url
              slug
              priority
              sitemapLevel
              metaTitle
              metaDescription
              lastSyncedAt
              createdAt
              updatedAt
              publishedAt
            }
          }
        }
      }
    `,
  };

  const response = await strapiCall(payload, true);
  if (response.status === 200) {
    const containers = response?.data?.data?.seoContainers?.data;
    return containers || [];
  }

  return [];
};

const getHomepageBanner = async (params = {}) => {
  const payload = {
    query: `
      {
        jswHomepageBanner {
          data {
            id
            attributes {
              title
              buttonText
              companyLink
              webImage {
                data {
                  attributes {
                    url
                  }
                }
              }
              mobileImage {
                data {
                  attributes {
                    url
                  }
                }
              }
            }
          }
        }
      }
    `,
  };

  const response = await strapiCall(payload);

  if (response.status === 200) {
    const { data } = response?.data?.data?.jswHomepageBanner || {};
    return data ?? null;
  } else {
    return null;
  }
};

const getCompanyListBanner = async (params = {}) => {
  const payload = {
    query: `
      {
        jswCompanyListBanner {
          data {
            id
            attributes {
              title
              companyLink
              webVideo {
                data {
                  attributes {
                    url
                  }
                }
              }
              mobileVideo {
                data {
                  attributes {
                    url
                  }
                }
              }
              companyImage {
                data {
                  attributes {
                    url
                  }
                }
              }
            }
          }
        }
      }
    `,
  };

  const response = await strapiCall(payload);

  if (response.status === 200) {
    const { data } = response?.data?.data?.jswCompanyListBanner || {};
    return data ?? null;
  } else {
    return null;
  }
};

const triggerJobReload = (status) => async (dispatch) => {
  return dispatch({
    type: types.TRIGGER_JOB_RELOAD,
    jobReload: status,
  });
};

const updateJobListTab = (tab) => async (dispatch) => {
  return dispatch({
    type: types.UPDATE_JOB_LIST_TAB,
    tab: tab,
  });
};

const updateFypTab = (tab) => async (dispatch) => {
  return dispatch({
    type: types.UPDATE_FYP_TAB,
    tab: tab,
  });
};

const updateViewHistory = (state) => async (dispatch) => {
  return dispatch({
    type: types.UPDATE_VIEW_HISTORY,
    state: state,
  });
};

const updateHideJob =
  (params = {}) =>
    async (dispatch) => {
      dispatch({ type: types.HIDE_JOB });

      const jobId = params.jobId ?? "";
      const rejectionReason = params.rejectionReason ?? "";

      const payload = {
        query: `
        mutation {
          rejectJob(input: {jobId: "${jobId}", rejectionReason: "${rejectionReason}"}) {
            rejectJob {
              jobId
            }
          }
        }     
      `,
      };

      return api
        .apiCall("/api/job_seeker/v1/graphql", params, payload)
        .then(async (response) => {
          if (response.status === 200) {
            const { rejectJob } = response?.data?.data?.rejectJob;

            if (rejectJob != null) {
              return dispatch({
                type: types.HIDE_JOB_SUCCESS,
              });
            }
            if (rejectJob === null) {
              return dispatch({
                type: types.HIDE_JOB_FAILED,
              });
            }
          } else {
            return dispatch({
              type: types.HIDE_JOB_FAILED,
            });
          }
        });
    };

const undoHideJob =
  (params = {}) =>
    async (dispatch) => {
      dispatch({ type: types.UNDO_HIDE_JOB });

      const payload = {
        query: `
          mutation {
            undoRejection(input: {jobId: "${params.jobId}"}) {
              success
        }
      }
    `,
      };
      return api
        .apiCall("/api/job_seeker/v1/graphql", params, payload)
        .then(async (response) => {
          if (response.status === 200) {
            const { success } = response?.data?.data?.undoRejection;
            if (success) {
              return dispatch({
                type: types.UNDO_HIDE_JOB_SUCCESS,
              });
            }
          } else {
            return dispatch({
              type: types.UNDO_HIDE_JOB_FAILED,
            });
          }
        });
    };

const updateFeedbackHideJob =
  (params = {}) =>
    async (dispatch) => {
      dispatch({ type: types.FEEDBACK_HIDE_JOB });

      const jobId = params.jobId ?? "";
      const rejectionReason = params.rejectionReason ?? "";

      const payload = {
        query: `
          mutation {
            updateRejectionReason(input: {jobId: "${jobId}",rejectionReason: "${rejectionReason}"}) {
              rejectJob {
                id
                rejectionReason
              }
            }
          }
        `,
      };

      return api
        .apiCall("/api/job_seeker/v1/graphql", params, payload)
        .then(async (response) => {
          if (response.status === 200) {
            const { rejectJob } = response?.data?.data?.updateRejectionReason;
            if (rejectJob != null) {
              return dispatch({
                type: types.FEEDBACK_HIDE_JOB_SUCCESS,
              });
            }
          } else {
            return dispatch({
              type: types.FEEDBACK_HIDE_JOB_FAILED,
            });
          }
        });
    };

const triggerFollowUpEmail = async (params = {}) => {
  const { token } = params;

  const payload = {
    query: `
        mutation {
          interviewPrepFollowUp(input: { token: "${token}" }) {
            clientMutationId
            message
          }
        }
      `,
  };

  try {
    const response = await api.apiCall(
      "/api/job_seeker/v1/graphql",
      params,
      payload,
      true
    );

    if (response.data?.errors?.length > 0) {
      const graphQLError = response.data.errors[0];
      return { error: graphQLError.message || "GraphQL error" };
    }

    if (response.status === 200) {
      const data = response.data.data?.interviewPrepFollowUp;
      if (!data || (!data.message && !data.clientMutationId)) {
        return { error: "Application not found or no response message." };
      }
      return data;
    }

    return { error: "Unexpected response status." };
  } catch (error) {
    return { error: error.message || "Network error" };
  }
};

const getJobListAds = async (params = {}) => {
  const payload = {
    query: `
      {
        jswJobListPageAd {
          data {
            id
            attributes {
              default
              customLink
              jlpAdWeb {
                data {
                  attributes {
                    url
                  }
                }
              }
              jlpAdTablet {
                data {
                  attributes {
                    url
                  }
                }
              }
              jlpAdMobile {
                data {
                  attributes {
                    url
                  }
                }
              }
            }
          }
        }
      }
    `,
  };

  const response = await strapiCall(payload);

  if (response.status === 200) {
    const { data } = response?.data?.data?.jswJobListPageAd || {};
    return data ?? null;
  } else {
    return null;
  }
};

export {
  applyJob,
  applyJobs,
  applyJobsBySpecialisation,
  bookmarkJob,
  bookmarkSingleJobCompany,
  bulkApplyJobs,
  cancelJobApplication,
  clearAppliedJobs,
  clearArchivedJobs,
  clearBookmarkJobList,
  clearBulkAppliedJobs,
  clearBulkJob,
  clearJobAlerts,
  clearJobList,
  clearJobSuggestion,
  clearPendingJobs,
  clearSearchSuggestions,
  clearTotalBulkAppliedJobs,
  closeAshleyErrorDialog,
  createJobAlert,
  deleteJobAlert,
  disableAllJobAlerts,
  fetchAppliedJobs,
  fetchArchivedJobs,
  fetchAshleyBackupJobs,
  fetchInternshipBannerContent,
  fetchJobBookmarkList,
  fetchPendingJobs,
  fetchSearchSuggestions,
  getAshleyAwsRecommendedJobs,
  getAshleyRecommendedJobs,
  getBulkJob,
  getCmsLocations,
  getCmsSitemap,
  getCmsSpecialisations,
  getCmsSubSpecialisation,
  getCompanyListBanner,
  getCuratedJobs,
  getExperienceLevels,
  getExperienceLevelsWithoutRedux,
  getFeaturedJobs,
  getFooterCompanyWriteup,
  getHomepageAds,
  getHomepageBanner,
  getHomepageLogo,
  getInternshipBankJobs,
  getInternshipJobs,
  getJobAlert,
  getJobAlerts,
  getJobAppliedAndBookmarkStatus,
  getJobListAds,
  getJobs,
  getJobsByTracksWithoutRedux,
  getJobsCampaign,
  getJobsForCima,
  getJobsForJobsfeed,
  getJobsForSitemap,
  getJobSimilarJobsV1,
  getJobsMicrosite,
  getJobsParams,
  getJobsWithoutRedux,
  getJobTrackSlugWithoutRedux,
  getJobTypes,
  getJobTypesWithoutRedux,
  getJobV1,
  getJobWithoutRedux,
  getPostApplyRecommendedJobs,
  getPostApplyRecommendedJobsBoost,
  getPostApplyRecommendedJobsTemp,
  getPostApplyRecommendedJobsWithoutRedux,
  getRecentlyViewedJobs,
  getRecommendedJobs,
  getSpecialisation,
  getSpecialisationWithoutRedux,
  getStateRegions,
  getStateRegionWithoutRedux,
  getTracks,
  getTracksWithoutRedux,
  getTrendingChip,
  getTrendingJob,
  getTrendingJobKeywords,
  postGaSessionId,
  replaceJobAlert,
  saveJob,
  triggerFollowUpEmail,
  triggerJobReload,
  unBookmarkJob,
  unBookmarkSingleJobCompany,
  undoHideJob,
  unSaveJob,
  updateAshleyInfoDialog,
  updateBookmarkedJobInList,
  updateBookmarkedRecommendedJobs,
  updateCheckingUrlParamForJobFilterStatus,
  updateFeedbackHideJob,
  updateFetchingForYou,
  updateFetchingJobListStatus,
  updateForYouPaginationCounter,
  updateFypTab,
  updateHideJob,
  updateHoverJob,
  updateInternshipFilterButtonStatus,
  updateJobAlert,
  updateJobAlertEmail,
  updateJobAlertToggle,
  updateJobId,
  updateJobList,
  updateJobListFilter,
  updateJobListFilterLanding,
  updateJobListPage,
  updateJobListSearchKeyword,
  updateJobListTab,
  updateJobSearchKeyword,
  updateMissingCountryPreference,
  updateSearchFeedback,
  updateSearchType,
  updateSelectAllJobFilter,
  updateSelectedBulkJobs,
  updateSelectedJob,
  updateSelectedJobId,
  updateShowJobAlertDialog,
  updateShowJobAlertSuccessDialog,
  updateViewHistory,
  visitorAddJobAlert,
  visitorUnsubscribeJobAlert
};

